import { Injectable, signal, inject, computed, Injector } from '@angular/core';
import { supabase } from '../supabase';
import { Procedure, QuotaPool, ProcedureQuota, ProcedureLocation, Executor, AgendaSlot, Appointment, PatientProcess, QuotaStat, ExamGroupMember, ImportReviewRow } from '../models';
import { normalizeCategory } from './db.service';
import { LoadingService } from './loading.service';
import { ToastService } from './toast.service';
import { AuditService } from './audit.service';
import { AppointmentService } from './appointment.service';
import { ContextService } from './context.service';
import { DateUtil } from '../utils/date.util';

export interface DistributeProgressInfo {
    current: number;
    total: number;
    procedureName: string;
    promotedCount: number;
    percent: number;
    status: 'analyzing' | 'processing' | 'finishing' | 'completed' | 'error';
    message?: string;
}

@Injectable({
    providedIn: 'root'
})
export class QuotaService {
    private loadingService = inject(LoadingService);
    private toast = inject(ToastService);
    private auditService = inject(AuditService);
    private apptService = inject(AppointmentService);
    private injector = inject(Injector);
    private get context(): ContextService {
        return this.injector.get(ContextService);
    }
    private static rpcWaitTimesUnavailable = false;

    public proceduresTable = signal<Procedure[]>([]);
    public quotaPoolsTable = signal<QuotaPool[]>([]);
    public procedureQuotasTable = signal<ProcedureQuota[]>([]);
    public locationsTable = signal<ProcedureLocation[]>([]);
    public executorsTable = signal<Executor[]>([]);
    public quotaStatsData = signal<QuotaStat[]>([]);

    public quotaStats = computed(() => {
        const pools = this.quotaPoolsTable();
        return pools.map(p => ({
            id: p.procedureId,
            name: p.procedureName,
            category: p.category,
            authorized: p.totalAuthorized,
            distributed: p.distributedCount,
            booked: p.bookedCount,
            waiting: 0, // Fill via refresh if needed
            available: p.totalAuthorized - p.bookedCount
        }));
    });

    /**
     * Inicializa as quotas para um novo mês.
     */
    async initQuotasForMonth(municipalityId: string, monthRef: string) {
        try {
            this.loadingService.set(true);
            const normalizedMonthRef = monthRef.length === 7 ? `${monthRef}-01` : monthRef;

            const { data, error } = await supabase.rpc('init_quotas_for_month', {
                p_municipality_id: municipalityId,
                p_month_ref: normalizedMonthRef
            });

            if (error) throw error;

            this.toast.show('Quotas inicializadas com sucesso.', 'success');
            return data;
        } catch (err: any) {
            this.toast.show(err.message || 'Erro ao inicializar quotas.', 'error');
            throw err;
        } finally {
            this.loadingService.set(false);
        }
    }

    async refreshProcedures(municipalityId: string | null) {
        if (!municipalityId) {
            this.proceduresTable.set([]);
            return;
        }

        const { data: procs } = await supabase
            .from('procedures')
            .select('*')
            .eq('municipality_id', municipalityId)
            // Filtrar apenas procedimentos ativos (não excluídos logicamente)
            .eq('is_active', true)
            .order('id', { ascending: true });

        if (procs) {
            this.proceduresTable.set(procs.map(p => ({
                id: p.id, name: p.name, category: p.category, complexity: p.complexity,
                monthlyLimit: p.monthly_limit, requiresPreSurgery: p.requires_pre_surgery,
                preSurgeryCount: p.pre_surgery_count, latestProtocol: p.latest_protocol,
                municipalityId: p.municipality_id, isImaging: p.is_imaging,
                isOnDemand: p.is_on_demand,
                isGroup: p.is_group ?? false,
                isLocallyExecuted: p.is_locally_executed ?? false,
                isActive: p.is_active ?? true,
                auditTrail: p.audit_trail, hasOrientation: p.has_orientation,
                orientationText: p.orientation_text
            })));
        }
    }

    async refreshLocations(municipalityId: string | null) {
        if (!municipalityId) {
            this.locationsTable.set([]);
            return;
        }

        const { data: locs } = await supabase
            .from('procedure_locations')
            .select('*')
            .eq('municipality_id', municipalityId)
            .neq('is_active', false);

        if (locs) this.locationsTable.set(locs.map(l => this.mapDbToLocation(l)));
    }

    async refreshExecutors(municipalityId: string | null) {
        if (!municipalityId) {
            this.executorsTable.set([]);
            return;
        }

        const { data: execs } = await supabase
            .from('executors')
            .select('*')
            .eq('municipality_id', municipalityId)
            .neq('is_active', false);

        if (execs) this.executorsTable.set(execs.map(e => ({
            id: e.id,
            municipalityId: e.municipality_id,
            name: e.name,
            isActive: e.is_active
        })));
    }

    /**
     * Distribui quotas (cria slots de agenda) em lote.
     */
    async distributeQuotaBatch(params: {
        poolId: string,
        municipalityId: string,
        locationName: string,
        startTime: string,
        count: number,
        intervalMinutes: number,
        professionalName?: string
    }) {
        try {
            this.loadingService.set(true);
            const { data, error } = await supabase.rpc('distribute_quota_batch', {
                p_pool_id: params.poolId,
                p_municipality_id: params.municipalityId,
                p_location_name: params.locationName,
                p_start_time: params.startTime,
                p_count: params.count,
                p_interval_minutes: params.intervalMinutes,
                p_professional_name: params.professionalName
            });

            if (error) throw error;

            if (data && !data.success) {
                throw new Error(data.error || 'Erro na distribuição.');
            }

            this.toast.show(`${params.count} vagas distribuídas com sucesso.`, 'success');
            return data;
        } catch (err: any) {
            this.toast.show(err.message || 'Erro ao distribuir quotas.', 'error');
            throw err;
        } finally {
            this.loadingService.set(false);
        }
    }

    private getWeekKey(date: Date): string {
        const [start] = this.getWeekRange(date);
        return start.toISOString().split('T')[0];
    }

    private getWeekRange(date: Date): [Date, Date] {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday
        const start = new Date(d.setDate(diff));
        start.setHours(0, 0, 0, 0);

        const end = new Date(start);
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);

        return [start, end];
    }

    private prepareCandidates(procApps: Appointment[], procedures: Procedure[]): Appointment[] {
        const bucketUrgency: Appointment[] = [];
        const bucketPriority: Appointment[] = [];
        const bucketNormal: Appointment[] = [];

        procApps.forEach((a: Appointment) => {
            if (a.urgencyPriority) bucketUrgency.push(a);
            else if (a.priority === 'Normal') bucketNormal.push(a);
            else bucketPriority.push(a);
        });

        const sortByDate = (a: Appointment, b: Appointment) => new Date(a.registrationDate).getTime() - new Date(b.registrationDate).getTime();
        bucketUrgency.sort(sortByDate);
        bucketNormal.sort(sortByDate);
        bucketPriority.sort(sortByDate);

        const candidates: Appointment[] = [...bucketUrgency];
        let idxP = 0, idxN = 0;
        while (idxP < bucketPriority.length || idxN < bucketNormal.length) {
            if (idxP < bucketPriority.length) candidates.push(bucketPriority[idxP++]);
            if (idxN < bucketNormal.length) candidates.push(bucketNormal[idxN++]);
        }

        // Filtra candidatos de cirurgia que ainda não completaram o pré-operatório
        return candidates.filter(a => {
            if (!a.isSurgeryParent) return true;
            const proc = procedures.find(p => p.id === a.procedureId);
            return (a.preSurgeryCompleted || 0) >= (proc?.preSurgeryCount || 0);
        });
    }

    async loadQuotaStats(municipalityId: string, monthRef?: string, locationId?: string, excludeUbs?: boolean) {
        if (!municipalityId) return;
        
        const targetMonth = monthRef || (() => {
            const d = new Date();
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        })();

        try {
            const { data, error } = await supabase.rpc('get_quota_stats_with_waiting', {
                p_municipality_id: municipalityId,
                p_month_ref: targetMonth,
                p_location_id: locationId || null,
                p_exclude_ubs_categories: excludeUbs || false
            });

            if (error) {
                console.error('Error loading quota stats:', error);
                return;
            }

            if (data) {
                this.quotaStatsData.set(data as QuotaStat[]);
            }
        } catch (err) {
            console.error('[QuotaService] Quota Stats Load Error:', err);
        }
    }

    /**
     * Carrega os pools de quotas com estatísticas e mapeia para o modelo tipado.
     */
    async refreshQuotaPools(municipalityId: string, monthRef: string) {
        try {
            const normalizedMonthRef = monthRef.length === 7 ? `${monthRef}-01` : monthRef;

            const { data, error } = await supabase.rpc('get_quota_pools_with_stats', {
                p_municipality_id: municipalityId,
                p_month_ref: normalizedMonthRef
            });

            if (error) {
                console.warn('RPC get_quota_pools_with_stats failed, using direct table query:', error);

                // Fallback: Fetch Pools joining with procedures to avoid missing data
                const { data: pools, error: poolsError } = await supabase
                    .from('quota_pools')
                    .select('*, procedures(name, category, requires_pre_surgery, pre_surgery_count, is_imaging, is_on_demand, is_group)')
                    .eq('municipality_id', municipalityId)
                    .eq('month_ref', normalizedMonthRef)
                    .order('created_at', { ascending: false });

                if (poolsError) throw poolsError;

                const mapped = (pools as any[]).map((d: any) => ({
                    id: d.id,
                    procedureId: Number(d.procedure_id) || 0,
                    municipalityId: d.municipality_id,
                    monthRef: d.month_ref,
                    totalAuthorized: d.total_authorized,
                    distributedCount: d.distributed_count,
                    bookedCount: d.booked_count || 0,
                    procedureName: d.procedures?.name || d.procedure_name || '',
                    category: d.procedures?.category || d.category || '',
                    requiresPreSurgery: (d.procedures?.requires_pre_surgery ?? d.requires_pre_surgery) || false,
                    preSurgeryCount: (d.procedures?.pre_surgery_count ?? d.pre_surgery_count) || 0,
                    isImaging: (d.procedures?.is_imaging ?? d.is_imaging) || false,
                    isOnDemand: (d.procedures?.is_on_demand ?? d.is_on_demand) || false,
                    isGroup: (d.procedures?.is_group ?? d.is_group) || false,
                    isLocallyExecuted: (d.procedures?.is_locally_executed ?? d.is_locally_executed) || false
                }));
                this.quotaPoolsTable.set(mapped);
                return mapped;
            }

            if (data) {
                // Mapear snake_case do banco para camelCase do frontend
                const mapped = (data as any[]).map((d: any) => ({
                    id: d.id,
                    procedureId: Number(d.procedure_id),
                    municipalityId: d.municipality_id,
                    monthRef: d.month_ref,
                    totalAuthorized: d.total_authorized,
                    distributedCount: d.distributed_count,
                    bookedCount: d.booked_count || 0,
                    procedureName: d.procedure_name,
                    category: d.procedure_category,
                    requiresPreSurgery: d.procedure_requires_pre_surgery,
                    preSurgeryCount: d.procedure_pre_surgery_count,
                    isImaging: d.procedure_is_imaging,
                    isOnDemand: d.procedure_is_on_demand,
                    isGroup: d.procedure_is_group ?? false,
                    isLocallyExecuted: d.procedure_is_locally_executed ?? false,
                    hasOrientation: d.procedure_has_orientation ?? false,
                    orientationText: d.procedure_orientation_text ?? ''
                }));
                this.quotaPoolsTable.set(mapped);
                return mapped;
            }
            return [];
        } catch (err) {
            console.error('[QuotaService] Erro ao atualizar quotas:', err);
            return [];
        }
    }

    async getPoolSlots(poolId: string) {
        // First get the slots
        const { data: slots, error } = await supabase
            .from('agenda_slots')
            .select(`
                *,
                patient:patients(name),
                executor:executors(name),
                appointment:appointments(schedule_date, schedule_time)
            `)
            .eq('pool_id', poolId)
            .neq('is_active', false)
            .order('start_time', { ascending: true });

        if (error) throw error;

        // Get unique parent procedure IDs
        const parentProcIds = [...new Set(slots?.map(s => s.parent_procedure_id).filter(Boolean))];

        // Fetch parent procedures if any
        let parentProcs: any[] = [];
        if (parentProcIds.length > 0) {
            const { data: procs } = await supabase
                .from('procedures')
                .select('id, name')
                .in('id', parentProcIds);
            parentProcs = procs || [];
        }

        const parentProcMap = new Map(parentProcs.map(p => [p.id, p.name]));

        return (slots || []).map((s: any) => ({
            id: s.id,
            poolId: s.pool_id,
            location: s.location_name,
            startTime: s.start_time,
            endTime: s.end_time,
            scheduleDate: s.appointment?.schedule_date || null,
            scheduleTime: s.appointment?.schedule_time || null,
            professional: s.professional_name,
            isAvailable: s.is_available,
            patientName: s.patient?.name,
            executorName: s.executor?.name,
            executorId: s.executor_id,
            parentProcedureId: s.parent_procedure_id,
            parentProcedureName: s.parent_procedure_id ? parentProcMap.get(s.parent_procedure_id) : null
        }));
    }

    async createQuotaPool(proc: any, initialLimit: number, municipalityId: string, monthRef: string) {
        // 1. Create/Get Procedure
        const { data: existingProc } = await supabase
            .from('procedures')
            .select('id')
            .eq('name', proc.name)
            .eq('municipality_id', municipalityId)
            .maybeSingle();

        let procedureId: number;
        if (existingProc) {
            procedureId = existingProc.id;
        } else {
            const operatorId = this.context.currentOperator()?.id || 0;
            const res = await this.addProcedure({ ...proc, municipalityId: municipalityId }, operatorId);
            procedureId = res.procedure.id;
        }

        // 2. Create Quota Pool
        const normalizedMonthRef = monthRef.substring(0, 7) + '-01';
        const { data: pool, error } = await supabase
            .from('quota_pools')
            .insert({
                procedure_id: procedureId,
                municipality_id: municipalityId,
                month_ref: normalizedMonthRef,
                total_authorized: initialLimit,
                distributed_count: 0
            })
            .select()
            .single();

        if (error) throw error;
        return pool;
    }

    async createMonthlyPool(data: Partial<QuotaPool>): Promise<QuotaPool> {
        if (!data.procedureId || !data.municipalityId || !data.monthRef) {
            throw new Error('Dados insuficientes para criar pool mensal.');
        }

        const normalizedMonthRef = data.monthRef.length === 7 ? `${data.monthRef}-01` : data.monthRef;

        const { data: newPool, error } = await supabase
            .from('quota_pools')
            .upsert({
                procedure_id: data.procedureId,
                municipality_id: data.municipalityId,
                month_ref: normalizedMonthRef,
                total_authorized: data.totalAuthorized || 0
            }, {
                onConflict: 'procedure_id,municipality_id,month_ref'
            })
            .select()
            .single();

        if (error) throw error;
        return {
            ...data,
            id: newPool.id,
            totalAuthorized: newPool.total_authorized,
            distributedCount: 0,
            bookedCount: 0
        } as QuotaPool;
    }

    async deleteProcedure(idStr: string, procedureId: number) {
        let deletedName = '';
        let deletedCategory = '';
        let procedureIdToLog = Number(procedureId);

        // Identifica se o ID é virtual (gerado pelo frontend para pools inexistentes no mês)
        const isVirtualId = idStr && idStr.startsWith('pool-');

        if (idStr && idStr !== '0' && !isVirtualId) {
            // Caso 1: ID é um UUID real de um Quota Pool
            const { data: pool, error: poolFetchErr } = await supabase
                .from('quota_pools')
                .select('procedure_id, procedure_name, category')
                .eq('id', idStr)
                .maybeSingle();

            if (poolFetchErr) console.warn('[QuotaService] Erro ao buscar pool real:', poolFetchErr);

            if (pool) {
                deletedName = pool.procedure_name || 'Desconhecido';
                deletedCategory = pool.category || 'Outros';
                procedureIdToLog = pool.procedure_id || procedureIdToLog;
            }
        } else {
            // Caso 2: ID Virtual ou Procedure ID direto
            const { data: proc, error: procFetchErr } = await supabase
                .from('procedures')
                .select('name, category')
                .eq('id', procedureIdToLog)
                .maybeSingle();

            if (procFetchErr) console.warn('[QuotaService] Erro ao buscar procedimento:', procFetchErr);

            if (proc) {
                deletedName = proc.name;
                deletedCategory = proc.category;
            }
        }

        // EXCLUSáO LÓGICA: Marcar procedimento como inativo em vez de deletar fisicamente
        // Isso impede que procedimentos "excluídos" sejam contados no dashboard
        const { error: updateErr } = await supabase
            .from('procedures')
            .update({ is_active: false })
            .eq('id', procedureIdToLog);

        if (updateErr) throw updateErr;

        // Atualização dos Signals Locais para refletir na UI imediatamente
        this.quotaPoolsTable.update(pools => pools.filter(p => String(p.id) !== idStr && p.procedureId !== procedureIdToLog));
        this.proceduresTable.update(procs => procs.filter(p => p.id !== procedureIdToLog));

        return { deletedName, deletedCategory, procedureId: procedureIdToLog };
    }

    async initializeMonthlyQuotas(municipalityId: string) {
        const { data, error } = await supabase.rpc('initialize_monthly_quotas', {
            p_municipality_id: municipalityId
        });
        if (error) throw error;
        return data;
    }


    async updateQuotaPoolLimit(poolId: string, newLimit: number) {
        const { data, error } = await supabase
            .from('quota_pools')
            .update({ total_authorized: newLimit })
            .eq('id', poolId)
            .select()
            .maybeSingle();

        if (error) throw error;
        return data;
    }

    /**
     * Limpa as distribuições e slots disponíveis de um pool para permitir sobrescrita.
     * Retorna o novo distributed_count baseado nos slots que restaram (agendados).
     */
    async clearPoolForRewrite(poolId: string): Promise<number> {
        // 1. Limpar apenas slots DISPONÍVEIS anteriores (nunca deletar slots com agendamentos ativos)
        await supabase
            .from('agenda_slots')
            .delete()
            .eq('pool_id', poolId)
            .eq('is_available', true);

        // 2. Limpar templates de distribuição anteriores
        await supabase
            .from('quota_distribution_templates')
            .delete()
            .eq('pool_id', poolId);

        // 3. Recalcular distributed_count filtrando apenas slots ATIVOS que sobreviveram
        //    (is_active=false são slots excluídos logicamente - não devem ser contados)
        const { data: countData } = await supabase
            .from('agenda_slots')
            .select('id')
            .eq('pool_id', poolId)
            .neq('is_active', false);

        const remainingCount = countData?.length || 0;

        // 4. Atualizar pool com contagem correta
        await supabase
            .from('quota_pools')
            .update({
                distributed_count: remainingCount
            })
            .eq('id', poolId);

        return remainingCount;
    }


    /**
     * Adiciona um novo procedimento no Supabase com protocolo e auditoria.
     */
    async addProcedure(proc: Omit<Procedure, 'id' | 'latestProtocol' | 'protocolHistory'>, operatorId: number, targetMunicipalityId?: string): Promise<{ procedure: Procedure, protocol: string }> {
        const munId = targetMunicipalityId || this.context.currentMunicipalityId();
        if (!munId) {
            throw new Error("Municipality ID is required to create a procedure.");
        }

        // 1. Save to Supabase first to get ID
        const { data: inserted, error } = await supabase
            .from('procedures')
            .insert({
                name: proc.name?.trim(),
                category: proc.category || 'CONSULTA BÁSICA',
                complexity: proc.complexity || 'Média',
                monthly_limit: proc.monthlyLimit ?? 0,
                requires_pre_surgery: proc.requiresPreSurgery ?? false,
                pre_surgery_count: proc.preSurgeryCount || 0,
                municipality_id: munId,
                is_imaging: proc.isImaging ?? false,
                is_on_demand: proc.isOnDemand ?? false,
                is_group: proc.isGroup ?? false,
                is_locally_executed: proc.isLocallyExecuted ?? false,
                has_orientation: proc.hasOrientation ?? false,
                orientation_text: proc.orientationText || null,
                is_active: true
            })
            .select()
            .single();

        if (error) {
            console.error('[QuotaService] Erro ao criar procedimento no Supabase:', error);
            throw error;
        }

        // 2. Gera Protocolo Fiscal
        let protocol = `PROT-${Date.now()}`;
        try {
            protocol = await this.auditService.gerarProtocolo(
                'CADASTRO_PROCEDIMENTO' as any,
                'PROCEDIMENTO',
                inserted.id,
                operatorId,
                undefined,
                undefined,
                `Cadastro de novo procedimento: ${proc.name}`
            );
        } catch (protErr) {
            console.warn('[QuotaService] Aviso ao gerar protocolo fiscal para procedimento:', protErr);
        }

        // 3. Atualiza registro com o protocolo gerado se disponível
        try {
            await supabase.from('procedures').update({ latest_protocol: protocol }).eq('id', inserted.id);
        } catch (updErr) {
            console.warn('[QuotaService] Aviso ao atualizar latest_protocol do procedimento:', updErr);
        }

        const newProc: Procedure = {
            id: inserted.id,
            name: inserted.name,
            category: inserted.category,
            complexity: inserted.complexity,
            monthlyLimit: inserted.monthly_limit,
            requiresPreSurgery: inserted.requires_pre_surgery,
            preSurgeryCount: inserted.pre_surgery_count,
            latestProtocol: protocol,
            protocolHistory: [protocol],
            municipalityId: inserted.municipality_id,
            isImaging: inserted.is_imaging,
            isOnDemand: inserted.is_on_demand,
            isGroup: inserted.is_group,
            isLocallyExecuted: inserted.is_locally_executed ?? false,
            hasOrientation: inserted.has_orientation,
            orientationText: inserted.orientation_text
        };

        // 4. Update Local State
        this.proceduresTable.update(current => [...current, newProc]);

        // 5. Registra Auditoria
        try {
            const procMunId = this.context.currentMunicipalityId();
            const dadosProcedimento = {
                nomeProcedimento: newProc.name,
                categoria: newProc.category,
                municipio: procMunId || '-',
                limiteMensal: newProc.monthlyLimit,
                requiresPreSurgery: newProc.requiresPreSurgery,
                preSurgeryCount: newProc.preSurgeryCount,
                ativo: true,
                locallyExecuted: newProc.isLocallyExecuted
            };
            
            await this.auditService.registrarAuditoria(
                protocol,
                'CRIACAO' as any,
                `Procedimento cadastrado no sistema: ${newProc.name} [${newProc.category}]`,
                operatorId,
                undefined,
                null,
                dadosProcedimento
            );
        } catch (audErr) {
            console.warn('[QuotaService] Erro não bloqueante ao registrar auditoria de procedimento:', audErr);
        }

        return { procedure: newProc, protocol };
    }

    /**
     * Atualiza um procedimento existente com sincronização e auditoria.
     */
    async updateProcedure(updated: Procedure, operatorId: number): Promise<string> {
        const currentMun = this.context.currentMunicipalityId();
        const oldProc = this.proceduresTable().find(p => p.id === updated.id);
        const protocol = `LOG-PROC-${Date.now()}-${Math.floor(1000 + Math.random() * 9000)}`;

        const { error } = await supabase.from('procedures').update({
            name: updated.name,
            category: updated.category,
            complexity: updated.complexity,
            monthly_limit: updated.monthlyLimit,
            requires_pre_surgery: updated.requiresPreSurgery,
            pre_surgery_count: updated.preSurgeryCount,
            is_imaging: updated.isImaging,
            is_on_demand: updated.isOnDemand,
            is_group: updated.isGroup ?? false,
            is_locally_executed: updated.isLocallyExecuted ?? false,
            has_orientation: updated.hasOrientation,
            orientation_text: updated.orientationText,
        }).eq('id', updated.id);

        if (error) {
            console.error('[QuotaService] Erro ao atualizar procedimento:', error);
            throw error;
        }

        // 2. Sync changes to quota_pools table (denormalized fields + update total_authorized)
        // CRITICAL FIX: Add municipality filter to avoid RLS issues on GLOBAL procedures
        // and handle the CHECK constraint (total_authorized >= distributed_count)

        let poolUpdateQuery = supabase.from('quota_pools').update({
            procedure_name: updated.name,
            category: updated.category,
            requires_pre_surgery: updated.requiresPreSurgery,
            pre_surgery_count: updated.preSurgeryCount,
            is_on_demand: updated.isOnDemand,
            is_group: updated.isGroup,
            is_imaging: updated.isImaging
        }).eq('procedure_id', updated.id);

        if (currentMun) {
            poolUpdateQuery = poolUpdateQuery.eq('municipality_id', currentMun);
        }

        const { error: syncError } = await poolUpdateQuery;

        if (syncError) {
            console.warn('[QuotaService] Erro ao sincronizar metadados no quota_pools:', syncError);
        }

        // 3. Handle total_authorized update with pre-emptive slot deletion if needed
        // We do this loop to ensure we don't violate the constraint total_authorized >= distributed_count
        let poolsQuery = supabase.from('quota_pools').select('id, municipality_id, distributed_count, total_authorized')
            .eq('procedure_id', updated.id);

        if (currentMun) {
            poolsQuery = poolsQuery.eq('municipality_id', currentMun);
        }

        const { data: poolsToSync } = await poolsQuery;

        if (poolsToSync) {
            for (const pool of poolsToSync) {
                // If we are reducing the limit below what's already distributed
                if (updated.monthlyLimit < pool.distributed_count) {
                    const diff = pool.distributed_count - updated.monthlyLimit;

                    // Try to delete available slots to reconcile
                    const { data: availableSlots } = await supabase
                        .from('agenda_slots')
                        .select('id')
                        .eq('pool_id', pool.id)
                        .eq('is_available', true)
                        .limit(diff);

                    if (availableSlots && availableSlots.length > 0) {
                        const ids = availableSlots.map(s => s.id);
                        const { error: delError } = await supabase.from('agenda_slots').delete().in('id', ids);

                        if (!delError) {
                            const newDistributed = pool.distributed_count - ids.length;
                            // Now we can update total_authorized safely (or as close as possible to the target limit)
                            await supabase.from('quota_pools')
                                .update({
                                    distributed_count: newDistributed,
                                    total_authorized: Math.max(updated.monthlyLimit, newDistributed)
                                })
                                .eq('id', pool.id);
                        }
                    } else {
                        // If NO available slots found, we still try to update total_authorized 
                        // but it might fail if limit < distributed_count. 
                        // We set it to the minimum possible (distributed_count).
                        await supabase.from('quota_pools')
                            .update({ total_authorized: Math.max(updated.monthlyLimit, pool.distributed_count) })
                            .eq('id', pool.id);
                    }
                } else {
                    // Increasing or keeping same: safe to update
                    await supabase.from('quota_pools')
                        .update({ total_authorized: updated.monthlyLimit })
                        .eq('id', pool.id);
                }
            }
        }

        // Update Local State
        this.proceduresTable.update(procs => procs.map(p => p.id === updated.id ? { ...p, ...updated } : p));

        const op = this.context.currentOperator();
        const userName = op?.name || 'ADMIN';

        await this.auditService.logAction('UPDATE_PROCEDURE', `Atualização: ${oldProc?.name}`, {
            previous: oldProc,
            new: updated,
            municipality: currentMun
        }, userName, protocol);

        return protocol;
    }



    /**
     * Mapeia dados do banco para o modelo Procedure.
     */
    private mapDbToProcedure(p: any): Procedure {
        return {
            id: p.id,
            name: p.name,
            category: p.category,
            complexity: p.complexity,
            monthlyLimit: p.monthly_limit,
            requiresPreSurgery: p.requires_pre_surgery,
            preSurgeryCount: p.pre_surgery_count,
            latestProtocol: p.latest_protocol,
            protocolHistory: p.protocol_history,
            municipalityId: p.municipality_id,
            isImaging: p.is_imaging,
            isGroup: p.is_group,
            isLocallyExecuted: p.is_locally_executed ?? false,
            isActive: p.is_active ?? true,
            hasOrientation: p.has_orientation,
            orientationText: p.orientation_text
        };
    }

    async getExecutors(municipalityId: string): Promise<Executor[]> {
        const { data, error } = await supabase
            .from('executors')
            .select('*')
            .eq('municipality_id', municipalityId)
            .order('name');

        if (error) throw error;
        return (data || []).map((e: any) => ({
            id: e.id,
            municipalityId: e.municipality_id,
            name: e.name,
            isActive: e.is_active,
            createdAt: e.created_at
        }));
    }

    async createExecutor(municipalityId: string, name: string): Promise<Executor> {
        const { data, error } = await supabase
            .from('executors')
            .insert({ municipality_id: municipalityId, name, is_active: true })
            .select()
            .single();

        if (error) throw error;
        return { id: data.id, municipalityId: data.municipality_id, name: data.name, isActive: data.is_active };
    }

    async deleteExecutor(id: string) {
        const { error } = await supabase
            .from('executors')
            .delete()
            .eq('id', id);

        if (error) throw error;
    }

    async updateExecutor(id: string, name: string): Promise<Executor> {
        const { data, error } = await supabase
            .from('executors')
            .update({ name })
            .eq('id', id)
            .select()
            .single();

        if (error) throw error;
        return { id: data.id, municipalityId: data.municipality_id, name: data.name, isActive: data.is_active };
    }

    async batchCreateSlots(slots: any[]) {
        const { data, error } = await supabase
            .from('agenda_slots')
            .insert(slots)
            .select();

        if (error) throw error;
        return data;
    }

    /**
     * Busca as cirurgias que utilizam um procedimento específico de pré-operatório.
     * @param preOpProcedureId ID do procedimento de pré-operatório
     */
    async getSurgeriesForPreOp(preOpProcedureId: number): Promise<{ id: number; name: string; category: string }[]> {
        console.log('[QuotaService] Calling get_surgeries_for_preop with procedureId:', preOpProcedureId);

        const { data, error } = await supabase.rpc('get_surgeries_for_preop', {
            p_procedure_id: preOpProcedureId
        });

        if (error) {
            console.error('[QuotaService] Erro ao buscar cirurgias para pré-op:', error);
            return [];
        }

        console.log('[QuotaService] RPC returned:', data);
        return data || [];
    }

    /**
     * Busca vagas disponíveis agrupadas por parent_procedure_id.
     * Usado para promoção automática de pré-operatórios.
     * @param procedureId ID do procedimento (pool)
     * @param municipalityId ID do município
     */
    async getAvailableSlotsByParentProcedure(procedureId: number, municipalityId: string) {
        // First, get pool IDs for this procedure
        const { data: pools, error: poolsError } = await supabase
            .from('quota_pools')
            .select('id')
            .eq('procedure_id', procedureId)
            .eq('municipality_id', municipalityId);

        if (poolsError || !pools || pools.length === 0) {
            return {};
        }

        const poolIds = pools.map(p => p.id);

        const { data, error } = await supabase
            .from('agenda_slots')
            .select(`
                id,
                pool_id,
                parent_procedure_id,
                parent_procedure:procedures!agenda_slots_parent_procedure_id_fkey(name)
            `)
            .eq('is_available', true)
            .in('pool_id', poolIds)
            .eq('municipality_id', municipalityId);

        if (error) throw error;

        // Group by parent_procedure_id
        const grouped: Record<string, { slotIds: string[], parentProcedureId: number | null, parentProcedureName: string | null }> = {};

        for (const slot of (data || []) as any[]) {
            const parentKey = slot.parent_procedure_id?.toString() || 'none';
            const parentProc = slot.parent_procedure as any;
            const parentName = parentProc?.name ?? null;
            if (!grouped[parentKey]) {
                grouped[parentKey] = {
                    slotIds: [],
                    parentProcedureId: slot.parent_procedure_id ?? null,
                    parentProcedureName: parentName
                };
            }
            grouped[parentKey].slotIds.push(slot.id);
        }

        return grouped;
    }

    async deleteAvailableSlotsByPool(poolId: string) {
        const { error } = await supabase
            .from('agenda_slots')
            .delete()
            .eq('pool_id', poolId)
            .eq('is_available', true);

        if (error) throw error;
    }

    /**
     * Exclui TODAS as vagas distribuídas de um pool (disponíveis e reguladas).
     * Zera o contador distributed_count do pool.
     */
    async deleteAllDistributedSlots(poolId: string) {
        // REGRA: Slots disponíveis (sem paciente) → deletar fisicamente.
        //        Slots ocupados (com agendamento ativo) → apenas liberar a vaga (o slot permanece).
        //        Isso evita corromper agendamentos existentes.

        // 1. Liberar slots OCUPADOS: remover vínculo de agendamento (o slot volta a ficar disponível)
        //    O trigger no banco (fn_safe_release_slot_on_appointment_change) cuida da automação,
        //    mas aqui fazemos a remoção manual para garantir via frontend também.
        const { data: occupiedSlots } = await supabase
            .from('agenda_slots')
            .select('id, appointment_id')
            .eq('pool_id', poolId)
            .eq('is_available', false)
            .neq('is_active', false);

        if (occupiedSlots && occupiedSlots.length > 0) {
            await supabase
                .from('agenda_slots')
                .update({
                    is_available: true,
                    appointment_id: null,
                    patient_id: null
                })
                .in('id', occupiedSlots.map(s => s.id));
        }

        // 2. Deletar fisicamente TODOS os slots disponíveis do pool
        // O Trigger trg_sync_quota_on_slot_change cuidará de decrementar o distributed_count 
        // para cada slot deletado, mantendo a sincronia perfeita.
        const { error: deleteError } = await supabase
            .from('agenda_slots')
            .delete()
            .eq('pool_id', poolId)
            .eq('is_available', true);

        if (deleteError) throw deleteError;
    }

    async getAvailableSlots(procedureId: number, municipalityId: string, monthRef?: string): Promise<AgendaSlot[]> {
        let query = supabase
            .from('agenda_slots')
            .select(`
                *,
                executor_id,
                pool:quota_pools!inner(procedure_id, month_ref),
                executor:executors(name)
            `)
            .eq('municipality_id', municipalityId)
            .eq('is_available', true)
            .eq('pool.procedure_id', procedureId);

        if (monthRef) {
            const normalizedMonthRef = monthRef.substring(0, 7) + '-01';
            query = query.eq('pool.month_ref', normalizedMonthRef);
        }

        const { data, error } = await query.order('start_time');

        if (error) throw error;

        // Resolve parent_procedure names for pre-operative slots
        const parentProcIds = [...new Set((data || []).map((s: any) => s.parent_procedure_id).filter(Boolean))];
        const parentProcMap = new Map<number, string>();
        if (parentProcIds.length > 0) {
            (this.proceduresTable() || []).forEach(p => {
                if (parentProcIds.includes(p.id)) parentProcMap.set(p.id, p.name);
            });
            const missingIds = parentProcIds.filter(id => !parentProcMap.has(id));
            if (missingIds.length > 0) {
                const { data: missingProcs } = await supabase
                    .from('procedures')
                    .select('id, name')
                    .in('id', missingIds);
                (missingProcs || []).forEach((p: any) => parentProcMap.set(p.id, p.name));
            }
        }

        return (data || []).map((s: any) => ({
            id: s.id,
            poolId: s.pool_id,
            municipalityId: s.municipality_id,
            executorId: s.executor_id,
            location: s.location_name,
            startTime: s.start_time,
            endTime: s.end_time,
            professional: s.professional_name,
            isAvailable: s.is_available,
            executorName: s.executor?.name,
            parentProcedureId: s.parent_procedure_id,
            parentProcedureName: s.parent_procedure_id ? parentProcMap.get(s.parent_procedure_id) || null : null
        })) as AgendaSlot[];
    }

    async getAvailableExecutorsWithQuotas(procedureId: number, municipalityId: string, monthRef?: string): Promise<{
        id: string;
        name: string;
        availableCount: number;
        surgeries?: {
            surgeryId: number;
            surgeryName: string;
            count: number;
        }[];
    }[]> {
        const slots = await this.getAvailableSlots(procedureId, municipalityId, monthRef);

        const executorMap = new Map<string, { 
            name: string; 
            count: number;
            surgeriesMap: Map<number, { surgeryId: number; surgeryName: string; count: number }>;
        }>();

        slots.forEach(slot => {
            const execId = slot.executorId;
            if (execId) {
                const current = executorMap.get(execId) || { 
                    name: slot.executorName || 'Desconhecido', 
                    count: 0,
                    surgeriesMap: new Map()
                };
                current.count += 1;
                if (slot.parentProcedureId) {
                    const surg = current.surgeriesMap.get(slot.parentProcedureId) || {
                        surgeryId: slot.parentProcedureId,
                        surgeryName: slot.parentProcedureName || 'Cirurgia Vinculada',
                        count: 0
                    };
                    surg.count += 1;
                    current.surgeriesMap.set(slot.parentProcedureId, surg);
                }
                executorMap.set(execId, current);
            }
        });

        return Array.from(executorMap.entries()).map(([id, data]) => ({
            id,
            name: data.name,
            availableCount: data.count,
            surgeries: Array.from(data.surgeriesMap.values())
        }));
    }

    async getPoolQuotas(procedureId: number, municipalityId: string, monthRef: string): Promise<{ total: number, distributed: number, booked: number, poolId: string }> {
        const normalizedMonthRef = monthRef.substring(0, 7) + '-01';

        const { data, error } = await supabase
            .from('quota_pools')
            .select('id, total_authorized, distributed_count, booked_count')
            .eq('procedure_id', procedureId)
            .eq('municipality_id', municipalityId)
            .eq('month_ref', normalizedMonthRef)
            .maybeSingle();

        if (error) throw error;
        if (!data) return { total: 0, distributed: 0, booked: 0, poolId: '' };

        return {
            total: data.total_authorized,
            distributed: data.distributed_count || 0,
            booked: data.booked_count || 0,
            poolId: data.id
        };
    }

    /**
     * Retorna os meses que possuem pools cadastrados ou slots disponíveis para o procedimento,
     * incluindo contagem de vagas ativas em cada mês para seleção rápida no agendamento em lote.
     */
    async getAvailableMonthsWithQuotas(procedureId: number, municipalityId: string): Promise<{
        monthRef: string;
        label: string;
        availableCount: number;
    }[]> {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1;
        const currentMonthRef = `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`;

        try {
            const [poolsRes, slotsRes] = await Promise.all([
                supabase
                    .from('quota_pools')
                    .select('id, month_ref, total_authorized, distributed_count')
                    .eq('procedure_id', procedureId)
                    .eq('municipality_id', municipalityId)
                    .order('month_ref', { ascending: true }),
                supabase
                    .from('agenda_slots')
                    .select(`
                        id,
                        pool:quota_pools!inner(month_ref)
                    `)
                    .eq('municipality_id', municipalityId)
                    .eq('is_available', true)
                    .eq('pool.procedure_id', procedureId)
            ]);

            const slotCountByMonth = new Map<string, number>();
            (slotsRes.data || []).forEach((s: any) => {
                const mRef = s.pool?.month_ref;
                if (mRef) {
                    const norm = mRef.substring(0, 7) + '-01';
                    slotCountByMonth.set(norm, (slotCountByMonth.get(norm) || 0) + 1);
                }
            });

            const monthRefsSet = new Set<string>();
            monthRefsSet.add(currentMonthRef);

            // Adiciona próximo mês e subsequente para facilitar planejamento futuro
            const nextMonthDate = new Date(currentYear, currentMonth, 1);
            const nextMonthRef = `${nextMonthDate.getFullYear()}-${String(nextMonthDate.getMonth() + 1).padStart(2, '0')}-01`;
            monthRefsSet.add(nextMonthRef);

            const next2MonthDate = new Date(currentYear, currentMonth + 1, 1);
            const next2MonthRef = `${next2MonthDate.getFullYear()}-${String(next2MonthDate.getMonth() + 1).padStart(2, '0')}-01`;
            monthRefsSet.add(next2MonthRef);

            (poolsRes.data || []).forEach((p: any) => {
                if (p.month_ref) {
                    const norm = p.month_ref.substring(0, 7) + '-01';
                    monthRefsSet.add(norm);
                }
            });

            const sortedRefs = Array.from(monthRefsSet).sort();

            return sortedRefs.map((mRef) => {
                const [yStr, mStr] = mRef.split('-');
                const d = new Date(Number(yStr), Number(mStr) - 1, 1);
                const monthName = d.toLocaleDateString('pt-BR', { month: 'long' });
                const capitalizedMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);
                const label = `${capitalizedMonth}/${yStr}`;

                return {
                    monthRef: mRef,
                    label,
                    availableCount: slotCountByMonth.get(mRef) || 0,
                };
            });
        } catch (err) {
            console.error('[QuotaService] Erro ao buscar meses com cotas:', err);
            return [
                { monthRef: currentMonthRef, label: 'Mês Atual', availableCount: 0 }
            ];
        }
    }



    public mapDbToLocation(l: any): ProcedureLocation {
        return {
            id: l.id,
            municipalityId: l.municipality_id,
            name: l.name,
            address: l.address,
            reference: l.reference,
            phone: l.phone,
            googleMapsUrl: l.google_maps_url,
            isActive: l.is_active,
            isUbs: l.is_ubs ?? false,
            n8nWebhookUrl: l.n8n_webhook_url,
            dailyQuotas: l.daily_quotas // Mapping DB snake_case to model camelCase
        };
    }

    async distributeSimpleQuota(
        poolId: string,
        executorId: string,
        municipalityId: string,
        count: number,
        parentProcedureId?: number
    ) {
        const { data, error } = await supabase.rpc('distribute_simple_quota', {
            p_pool_id: poolId,
            p_executor_id: executorId,
            p_municipality_id: municipalityId,
            p_count: count,
            p_parent_procedure_id: parentProcedureId ?? null
        });

        if (error) throw error;
        if (data && data.success === false) {
            throw new Error(data.error);
        }
        return data;
    }

    /**
     * Distribui vagas para múltiplos executores simultaneamente.
     * @param poolId ID do pool de cotas
     * @param distributions Array de objetos com executorId e quantity
     * @param municipalityId ID do município
     * @param autoRepeat Se true, salva como template para replicação automática
     * @param parentProcedureId ID da cirurgia pai (para vagas de pré-operatório)
     */
    async distributeMultiExecutor(
        poolId: string,
        distributions: { executorId: string; quantity: number }[],
        municipalityId: string,
        autoRepeat: boolean = true,
        parentProcedureId?: number
    ) {
        const { data, error } = await supabase.rpc('distribute_multi_executor', {
            p_pool_id: poolId,
            p_distributions: distributions,
            p_municipality_id: municipalityId,
            p_auto_repeat: autoRepeat,
            p_parent_procedure_id: parentProcedureId ?? null
        });

        if (error) throw error;
        if (data && data.success === false) {
            throw new Error(data.error);
        }
        return data;
    }

    /**
     * Replica as distribuições de template para o mês alvo.
     * @param municipalityId ID do município
     * @param targetMonth Data do mês alvo (YYYY-MM-01)
     */
    async replicateDistributions(
        municipalityId: string,
        targetMonth: string
    ) {
        const { data, error } = await supabase.rpc('replicate_distributions', {
            p_municipality_id: municipalityId,
            p_target_month: targetMonth
        });

        if (error) throw error;
        if (data && data.success === false) {
            throw new Error(data.error);
        }
        return data;
    }

    /**
     * Busca os totais de vagas distribuídas (slots na agenda), agrupadas por executor, para múltiplos pools.
     * Retorna um mapa onde a chave é o ID do Pool e o valor é um array de distribuições.
     */
    async getDistributionsByPools(poolIds: string[]) {
        if (!poolIds || poolIds.length === 0) return {};

        const { data, error } = await supabase
            .from('agenda_slots')
            .select('pool_id, executor_id')
            .in('pool_id', poolIds)
            .neq('is_active', false); // Ignorar slots excluídos logicamente

        if (error) {
            console.error('[QuotaService] Erro ao buscar distribuições em lote:', error);
            return {};
        }

        const map: Record<string, { executorId: string, count: number }[]> = {};

        // Inicializar o mapa
        poolIds.forEach(id => map[id] = []);

        if (data) {
            // Agrupar e contar
            const tempMap: Record<string, Record<string, number>> = {};

            data.forEach((slot: any) => {
                const poolId = slot.pool_id;
                const execId = slot.executor_id;

                if (execId) {
                    if (!tempMap[poolId]) tempMap[poolId] = {};
                    if (!tempMap[poolId][execId]) tempMap[poolId][execId] = 0;
                    tempMap[poolId][execId]++;
                }
            });

            // Converter para o formato final
            Object.keys(tempMap).forEach(poolId => {
                map[poolId] = Object.keys(tempMap[poolId]).map(execId => ({
                    executorId: execId,
                    count: tempMap[poolId][execId]
                }));
            });
        }

        return map;
    }

    /**
     * Busca templates de distribuição para um pool.
     */
    async getDistributionTemplates(poolId: string) {
        const { data, error } = await supabase.rpc('get_distribution_templates', {
            p_pool_id: poolId
        });

        if (error) throw error;
        return data || [];
    }

    // --- URGENCY & DISTRIBUTION LOGIC ---

    async markAsUrgency(appointmentId: number, justification?: { text: string, documentImage?: string }, operatorId?: number) {
        try {
            const { data: appData, error: fetchErr } = await supabase
                .from('appointments')
                .select('*')
                .eq('id', appointmentId)
                .single();

            if (fetchErr || !appData) throw fetchErr || new Error('Appointment not found');

            const { error: updateErr } = await supabase
                .from('appointments')
                .update({
                    urgency_priority: true,
                    priority: 'URGÊNCIA',
                    urgency_justification: {
                        text: justification?.text,
                        documentImage: justification?.documentImage,
                        justifiedBy: this.context.currentOperator()?.name || 'Sistema',
                        justifiedAt: new Date().toISOString()
                    },
                    urgency_document: justification?.documentImage
                })
                .eq('id', appointmentId);

            if (updateErr) throw updateErr;

            const dadosAuditoria = {
                nomePaciente: appData.patient?.name || appData.patient?.social_name || '-',
                cpf: appData.patient?.cpf || '-',
                procedimento: appData.procedure?.name || '-',
                statusAnterior: appData.status,
                novoStatus: appData.status,
                prioridade: 'URGÊNCIA',
                urgencia: true,
                justificativa: justification?.text || 'Não informada',
                imagem: justification?.documentImage || null,
                operador: this.context.currentOperator()?.name || 'Sistema',
                dataMarcacao: new Date().toLocaleString('pt-BR')
            };

            await this.auditService.registrarAuditoria(
                appData.latest_protocol || `URG-${appointmentId}`,
                'MUDANCA_STATUS',
                `Marcação de Urgência: Atendimento #${appointmentId}. Justificativa: ${justification?.text || 'Não informada'}`,
                operatorId || 0,
                undefined,
                null,
                dadosAuditoria
            );

            await this.promoteUrgencyAndRebalance(appointmentId);
            return true;
        } catch (err) {
            console.error('[QuotaService] Erro ao marcar urgência:', err);
            this.toast.show('Erro ao processar urgência', 'error');
            return false;
        }
    }

    async promoteUrgencyAndRebalance(appointmentId: number) {
        const { data: rawApp, error: appErr } = await supabase
            .from('appointments')
            .select('*')
            .eq('id', appointmentId)
            .single();

        if (appErr || !rawApp) return;

        const protocol = rawApp.latest_protocol || `LOG-URG-${new Date().getTime()}`;

        const { data: inScheduling, error: inSchedErr } = await supabase
            .from('appointments')
            .select('id')
            .eq('municipality_id', rawApp.municipality_id)
            .eq('procedure_id', rawApp.procedure_id)
            .eq('status', 'Em Agendamento')
            .order('registration_date', { ascending: false })
            .limit(1);

        if (!inSchedErr && inScheduling && inScheduling.length > 0) {
            const demotionCandidateId = inScheduling[0].id;
            if (demotionCandidateId !== appointmentId) {
                const { error: demoteErr } = await supabase
                    .from('appointments')
                    .update({ status: 'Aguardando' })
                    .eq('id', demotionCandidateId);

                if (!demoteErr) {
                    await this.auditService.logAction(
                        'UPDATE_STATUS_Demotion',
                        `Auto-Demoção: Paciente ${demotionCandidateId} voltou a 'Aguardando' (urgência ID ${appointmentId})`,
                        { appointmentId: demotionCandidateId, reason: 'Urgency Rebalancing' },
                        'SYSTEM',
                        protocol
                    );
                }
            }
        }

        const { error: promoteErr } = await supabase
            .from('appointments')
            .update({ status: 'Em Agendamento' })
            .eq('id', appointmentId);

        if (!promoteErr) {
            await this.auditService.logAction(
                'UPDATE_STATUS_PROMOTION',
                `Auto-Promoção: Paciente urgente ${appointmentId} → 'Em Agendamento'`,
                { appointmentId, reason: 'Urgency Marked' },
                'SYSTEM',
                protocol
            );
        }

        this.apptService.triggerQueueRefresh();
    }

    async distributeVacancies(
        municipalityId: string,
        targetMonth?: string,
        onProgress?: (progress: DistributeProgressInfo) => void
    ): Promise<{ count: number, breakdown: Record<string, number> }> {
        const role = this.context.currentUserRole();
        const operator = this.context.currentOperator();
        const hasUbsModule = this.context.currentMunicipality()?.enable_ubs_module === true;
        let totalPromoted = 0;
        const promotedBreakdown: Record<string, number> = {};

        onProgress?.({
            current: 0,
            total: 0,
            procedureName: 'Consultando cotas e fila...',
            promotedCount: 0,
            percent: 5,
            status: 'analyzing',
            message: 'Consultando cotas e demanda da fila...'
        });

        // 0. Resolve UBS Name if regulator is from a UBS
        let ubsLocationName: string | null = null;
        let categoryFilter: string | null = null;

        if (role === 'REGULADOR_UBS' && operator?.location_id && municipalityId) {
            const { data: loc } = await supabase
                .from('procedure_locations')
                .select('name')
                .eq('id', operator.location_id)
                .eq('municipality_id', municipalityId)
                .maybeSingle();
            ubsLocationName = loc?.name || null;
            categoryFilter = 'CONSULTA BÁSICA';
            console.log('[QuotaService] UBS Regulator context detected. Unit:', ubsLocationName);
        } else if (hasUbsModule && (role === 'ADMIN_MUNICIPAL' || role === 'OPERADOR_MUNICIPAL' || role === 'OPERADOR')) {
            // For municipal regulators, the RPC will naturally handle exclusions if we don't pass a specific category
            // but we want to ensure it skips Basic if the module is active.
            // Actually, the loop handles this, but let's make the RPC call more specific if possible.
        }

        const isMunicipalRegulator = role === 'ADMIN_MUNICIPAL' || role === 'OPERADOR_MUNICIPAL' || role === 'OPERADOR';
        const excludeUbs = hasUbsModule && isMunicipalRegulator;

        try {
            // 1. Busca estatísticas (Vacâncias vs Fila) - Now context-aware
            const { data: statsData, error: statsError } = await supabase.rpc('get_quota_stats_with_waiting', {
                p_municipality_id: municipalityId,
                p_month_ref: targetMonth,
                p_origin_location: ubsLocationName,
                p_category_filter: categoryFilter,
                p_exclude_ubs_categories: excludeUbs
            });

            if (statsError || !statsData) {
                console.error('[QuotaService] Erro ao buscar estatísticas para distribuição:', statsError);
                onProgress?.({
                    current: 0,
                    total: 0,
                    procedureName: 'Erro',
                    promotedCount: 0,
                    percent: 0,
                    status: 'error',
                    message: 'Erro ao buscar dados das cotas.'
                });
                return { count: 0, breakdown: {} };
            }

            const stats = statsData as QuotaStat[];
            const procs = this.proceduresTable();

            // 2. Filtra procedimentos elegíveis previamente para contagem precisa
            const eligibleStats = stats.filter(q => {
                if (q.available_count <= 0 || q.waiting_ready_count <= 0) return false;
                const normCat = normalizeCategory(q.category);
                const isBasic = normCat === 'CONSULTA BASICA';
                if (role === 'REGULADOR_UBS' && !isBasic) return false;
                if (isMunicipalRegulator && hasUbsModule && isBasic) return false;
                return true;
            });

            const totalEligible = eligibleStats.length;

            if (totalEligible === 0) {
                onProgress?.({
                    current: 0,
                    total: 0,
                    procedureName: 'Nenhuma vaga pendente',
                    promotedCount: 0,
                    percent: 100,
                    status: 'completed',
                    message: 'Não há vagas disponíveis ou pacientes na fila para promoção.'
                });
                return { count: 0, breakdown: {} };
            }

            onProgress?.({
                current: 0,
                total: totalEligible,
                procedureName: 'Organizando prioridades...',
                promotedCount: 0,
                percent: 10,
                status: 'processing',
                message: `Identificadas ${totalEligible} especialidade(s) com vagas disponíveis.`
            });

            let processedIndex = 0;

            // 3. Processa cada procedimento elegível
            for (const q of eligibleStats) {
                processedIndex++;
                const normCat = normalizeCategory(q.category);
                const isPreOp = normCat === 'PRÉ-OPERATÓRIO';

                const progressBasePercent = 10 + Math.round(((processedIndex - 0.7) / totalEligible) * 80);
                onProgress?.({
                    current: processedIndex,
                    total: totalEligible,
                    procedureName: q.procedure_name,
                    promotedCount: totalPromoted,
                    percent: Math.min(92, progressBasePercent),
                    status: 'processing',
                    message: `Analisando fila: ${q.procedure_name}`
                });

                // Se for PRÉ-OPERATÓRIO, buscar vagas agrupadas por cirurgia específica
                let slotsBySurgery: Record<string, { slotIds: string[], parentProcedureId: number | null, parentProcedureName: string | null }> = {};
                if (isPreOp) {
                    slotsBySurgery = await this.getAvailableSlotsByParentProcedure(q.procedure_id, municipalityId);
                }

                // 4. Busca Abrangente (Toda a fila via paginação)
                let waitingPatients: Appointment[] = [];
                let rangeStart = 0;
                const rangeSize = 1000;
                const MAX_SEARCH = 20000;

                while (waitingPatients.length < MAX_SEARCH) {
                    let query = supabase
                        .from('appointments')
                        .select('*')
                        .eq('municipality_id', municipalityId)
                        .eq('procedure_id', q.procedure_id)
                        .eq('status', 'Aguardando');

                    // 3. REGRA: Regulador de UBS só vê pacientes da sua unidade (origin_location)
                    if (role === 'REGULADOR_UBS' && ubsLocationName) {
                        query = query.eq('origin_location', ubsLocationName);
                    }

                    // Filtro de Segurança Cirúrgica: Apenas quem já concluiu pré-op (se aplicável)
                    const proc = this.proceduresTable().find(p => p.id === q.procedure_id);
                    if (proc?.requiresPreSurgery || isPreOp) {
                        query = query.gte('pre_surgery_completed', proc?.preSurgeryCount || 1);
                    }

                    const { data: batch, error: batchErr } = await query
                        .order('registration_date', { ascending: true })
                        .range(rangeStart, rangeStart + rangeSize - 1);

                    if (batchErr || !batch || batch.length === 0) break;
                    waitingPatients = [...waitingPatients, ...batch.map(a => this.apptService.mapDbToAppointment(a))];
                    if (batch.length < rangeSize) break;
                    rangeStart += rangeSize;
                }

                if (waitingPatients.length === 0) continue;

                // FILTRO CRÍTICO: Se há vagas com cirurgia específica vinculada, filtrar pacientes
                let eligiblePatients = waitingPatients;

                if (isPreOp) {
                    const hasSpecificSurgerySlots = Object.keys(slotsBySurgery).some(k => k !== 'none');

                    if (hasSpecificSurgerySlots) {
                        const appointmentsList = this.apptService.appointmentsTable();

                        eligiblePatients = waitingPatients.filter(patient => {
                            if (!patient.parentAppointmentId) {
                                return slotsBySurgery['none'] !== undefined;
                            }

                            const parentApp = appointmentsList.find(a => a.id === patient.parentAppointmentId);
                            if (!parentApp) return false;

                            const surgeryIdStr = parentApp.procedureId.toString();
                            return slotsBySurgery[surgeryIdStr] !== undefined;
                        });

                        console.log(`[QuotaService] Filtro de cirurgia aplicado: ${eligiblePatients.length}/${waitingPatients.length} pacientes elegíveis`);
                    }
                }

                if (eligiblePatients.length === 0) continue;

                // --- FASE 2: Ordenação de Ouro (Priority Score + Interleaving) ---
                const candidates = this.applyPriorityInterleaving(eligiblePatients);
                const toPromote = candidates.slice(0, q.available_count);

                if (toPromote.length === 0) continue;

                const idsToUpdate = toPromote.map(p => p.id);
                const protocol = await this.auditService.gerarProtocolo(
                    'DISTRIBUICAO_AUTOMATICA' as any,
                    'PROCEDIMENTO',
                    q.procedure_id,
                    0,
                    'Sistema',
                    'system',
                    `Distribuição automática de vagas: ${idsToUpdate.length} pacientes`
                );

                const { error: updateError } = await supabase
                    .from('appointments')
                    .update({
                        status: 'Em Agendamento',
                        latest_protocol: protocol
                    })
                    .in('id', idsToUpdate)
                    .eq('status', 'Aguardando');

                if (updateError) {
                    console.error(`[QuotaService] Erro ao promover pacientes:`, updateError);
                    continue;
                }

                totalPromoted += idsToUpdate.length;
                promotedBreakdown[q.procedure_name] = (promotedBreakdown[q.procedure_name] || 0) + idsToUpdate.length;

                const afterStepPercent = 10 + Math.round((processedIndex / totalEligible) * 80);
                onProgress?.({
                    current: processedIndex,
                    total: totalEligible,
                    procedureName: q.procedure_name,
                    promotedCount: totalPromoted,
                    percent: Math.min(94, afterStepPercent),
                    status: 'processing',
                    message: `${idsToUpdate.length} vaga(s) atribuídas em ${q.procedure_name}`
                });

                // --- FASE 3: Auditoria ---
                await this.auditService.logAction(
                    'DISTRIBUTE_VACANCIES_AUTO',
                    `Distribuição Automática: ${idsToUpdate.length} pacientes promovidos para '${q.procedure_name}'`,
                    { 
                        procedureId: q.procedure_id, 
                        procedureName: q.procedure_name,
                        count: idsToUpdate.length, 
                        protocol 
                    },
                    'SISTEMA',
                    protocol
                );
            }

            // 5. Atualiza dashboard e fila
            if (totalPromoted > 0) {
                onProgress?.({
                    current: totalEligible,
                    total: totalEligible,
                    procedureName: 'Sincronizando painel...',
                    promotedCount: totalPromoted,
                    percent: 96,
                    status: 'finishing',
                    message: 'Sincronizando painel e filas com o banco de dados...'
                });
                await this.loadQuotaStats(municipalityId, targetMonth);
                this.apptService.triggerQueueRefresh();
            }

            onProgress?.({
                current: totalEligible,
                total: totalEligible,
                procedureName: 'Distribuição finalizada!',
                promotedCount: totalPromoted,
                percent: 100,
                status: 'completed',
                message: `${totalPromoted} paciente(s) promovidos com sucesso!`
            });

            return { count: totalPromoted, breakdown: promotedBreakdown };

        } catch (err: any) {
            console.error('[QuotaService] Erro crítico na distribuição:', err);
            onProgress?.({
                current: 0,
                total: 0,
                procedureName: 'Erro',
                promotedCount: totalPromoted,
                percent: 100,
                status: 'error',
                message: err?.message || 'Ocorreu um erro durante a distribuição.'
            });
            return { count: totalPromoted, breakdown: promotedBreakdown };
        }
    }

    /**
     * Intercala pacientes prioritários e normais (1:1).
     */
    applyPriorityInterleaving(appointments: Appointment[]): Appointment[] {
        const bucketPriority: Appointment[] = [];
        const bucketNormal: Appointment[] = [];

        for (const a of appointments) {
            const score = this.getPriorityScore(a);
            if (score > 0) bucketPriority.push(a);
            else bucketNormal.push(a);
        }

        const sortByPriority = (a: Appointment, b: Appointment) => {
            const sA = this.getPriorityScore(a);
            const sB = this.getPriorityScore(b);
            if (sA !== sB) return sB - sA;
            const dateA = new Date(a.registrationDate).getTime();
            const dateB = new Date(b.registrationDate).getTime();
            if (dateA !== dateB) return dateA - dateB;
            return a.id - b.id;
        };

        const sortByDate = (a: Appointment, b: Appointment) => {
            const dateA = new Date(a.registrationDate).getTime();
            const dateB = new Date(b.registrationDate).getTime();
            if (dateA !== dateB) return dateA - dateB;
            return a.id - b.id;
        };

        bucketPriority.sort(sortByPriority);
        bucketNormal.sort(sortByDate);

        return this.interleave(bucketPriority, bucketNormal);
    }

    async getPatientQueuePosition(appointmentId: number, procedureId: number, municipalityId: string): Promise<number | null> {
        const { data, error } = await supabase
            .from('appointments')
            .select('*')
            .eq('procedure_id', procedureId)
            .eq('municipality_id', municipalityId)
            .in('status', ['Aguardando', 'Em Agendamento']);

        if (error || !data) {
            console.error('[QuotaService] Error fetching queue for position:', error);
            return null;
        }

        const mapped = data.map(d => this.apptService.mapDbToAppointment(d));
        const ordered = this.applyPriorityInterleaving(mapped);
        const index = ordered.findIndex(a => a.id === appointmentId);
        return index !== -1 ? index + 1 : null;
    }

    /**
     * Posição na fila APENAS entre pacientes "Aguardando" — usada para previsão.
     * Pacientes "Em Agendamento" já têm vaga reservada no mês corrente e não
     * devem ser contados como consumidores de capacidade futura.
     */
    async getPatientForecastPosition(appointmentId: number, procedureId: number, municipalityId: string): Promise<number | null> {
        const { data, error } = await supabase
            .from('appointments')
            .select('*')
            .eq('procedure_id', procedureId)
            .eq('municipality_id', municipalityId)
            .eq('status', 'Aguardando');

        if (error || !data) {
            console.error('[QuotaService] Error fetching forecast queue position:', error);
            return null;
        }

        const mapped = data.map(d => this.apptService.mapDbToAppointment(d));
        const ordered = this.applyPriorityInterleaving(mapped);
        const index = ordered.findIndex(a => a.id === appointmentId);
        return index !== -1 ? index + 1 : null;
    }

    async getAverageMonthlyUsage(procedureId: number, municipalityId: string): Promise<number | null> {
        const dateLimit = new Date();
        dateLimit.setDate(dateLimit.getDate() - 120);
        const { count, error } = await supabase
            .from('appointments')
            .select('*', { count: 'exact', head: true })
            .eq('procedure_id', procedureId)
            .eq('municipality_id', municipalityId)
            .not('schedule_date', 'is', null)
            .gte('created_at', dateLimit.toISOString());

        if (error) {
            console.error('[QuotaService] Error fetching average usage:', error);
            return null;
        }
        if (!count) return null;
        return count / 4;
    }

    /**
     * Retorna a capacidade mensal real do procedimento, buscando o total_authorized
     * do pool do mês atual (ou mais recente). Esta é a fonte correta para calcular
     * previsões pois reflete as vagas configuradas no quota manager.
     */
    async getMonthlyCapacity(procedureId: number, municipalityId: string): Promise<number | null> {
        const now = new Date();
        const monthRef = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;

        // Tenta o mês corrente primeiro
        const { data: currentPool, error: currentErr } = await supabase
            .from('quota_pools')
            .select('total_authorized')
            .eq('procedure_id', procedureId)
            .eq('municipality_id', municipalityId)
            .eq('month_ref', monthRef)
            .maybeSingle();

        if (!currentErr && currentPool?.total_authorized) {
            return currentPool.total_authorized;
        }

        // Fallback: pool mais recente disponível para este procedimento
        const { data: latestPool, error: latestErr } = await supabase
            .from('quota_pools')
            .select('total_authorized')
            .eq('procedure_id', procedureId)
            .eq('municipality_id', municipalityId)
            .order('month_ref', { ascending: false })
            .limit(1)
            .maybeSingle();

        if (!latestErr && latestPool?.total_authorized) {
            return latestPool.total_authorized;
        }

        return null;
    }


    async getAverageWaitTimeDays(procedureId: number | null, municipalityId: string): Promise<number | null> {
        if (!QuotaService.rpcWaitTimesUnavailable) {
            try {
                // Tenta RPC nativa otimizada
                const { data, error } = await supabase.rpc('get_average_wait_times', {
                    p_municipality_id: municipalityId || null,
                    p_procedure_id: procedureId || null
                });

                if (error) {
                    if (error.code === 'PGRST202' || error.message?.includes('not exist') || (error as any).status === 404) {
                        QuotaService.rpcWaitTimesUnavailable = true;
                    }
                } else if (data && data.general !== undefined) {
                    return data.general !== null ? Number(data.general) : null;
                }
            } catch (rpcErr) {
                QuotaService.rpcWaitTimesUnavailable = true;
            }
        }

        // Fallback em caso de indisponibilidade da RPC
        const dateLimit = new Date();
        dateLimit.setDate(dateLimit.getDate() - 120);
        const limitStr = dateLimit.toISOString().split('T')[0];
        
        let query = supabase
            .from('appointments')
            .select('status, registration_date, created_at, schedule_date, outcome_date')
            .eq('municipality_id', municipalityId)
            .in('status', ['Agendado', 'Realizado'])
            .or(`schedule_date.gte."${limitStr}",outcome_date.gte."${limitStr}"`);

        if (procedureId) {
            query = query.eq('procedure_id', procedureId);
        }

        const { data, error } = await query;

        if (error || !data || data.length === 0) {
            return null;
        }

        let totalDays = 0;
        let validCount = 0;
        for (const app of data) {
            const baseDate = new Date(app.registration_date || app.created_at);
            const endDateStr = app.status === 'Realizado' ? (app.outcome_date || app.schedule_date) : app.schedule_date;
            if (!endDateStr) continue;

            const endDate = new Date(endDateStr);
            const diffMs = Math.max(0, endDate.getTime() - baseDate.getTime());
            totalDays += diffMs / (1000 * 60 * 60 * 24);
            validCount++;
        }

        return validCount > 0 ? Math.round(totalDays / validCount) : null;
    }

    async getAllProceduresWaitTimes(municipalityId: string): Promise<{procedureId: number, procedureName: string, avgDays: number | null}[]> {
        const activeProcedures = this.proceduresTable().filter(p => p.isActive !== false);

        if (!QuotaService.rpcWaitTimesUnavailable) {
            try {
                // Tenta RPC nativa otimizada
                const { data, error } = await supabase.rpc('get_average_wait_times', {
                    p_municipality_id: municipalityId || null,
                    p_procedure_id: null
                });

                if (error) {
                    if (error.code === 'PGRST202' || error.message?.includes('not exist') || (error as any).status === 404) {
                        QuotaService.rpcWaitTimesUnavailable = true;
                    }
                } else if (data && Array.isArray(data.procedures) && data.procedures.length > 0) {
                    return data.procedures;
                }
            } catch (rpcErr) {
                QuotaService.rpcWaitTimesUnavailable = true;
            }
        }

        // Fallback em caso de indisponibilidade da RPC
        const dateLimit = new Date();
        dateLimit.setDate(dateLimit.getDate() - 120);
        const limitStr = dateLimit.toISOString().split('T')[0];

        const { data, error } = await supabase
            .from('appointments')
            .select('procedure_id, status, registration_date, created_at, schedule_date, outcome_date')
            .eq('municipality_id', municipalityId)
            .in('status', ['Agendado', 'Realizado'])
            .or(`schedule_date.gte."${limitStr}",outcome_date.gte."${limitStr}"`);

        if (error || !data) {
            return activeProcedures.map(p => ({
                procedureId: p.id,
                procedureName: p.name,
                avgDays: null
            }));
        }

        const statsByProc = new Map<number, { totalDays: number, count: number }>();
        for (const app of data) {
            const baseDate = new Date(app.registration_date || app.created_at);
            const endDateStr = app.status === 'Realizado' ? (app.outcome_date || app.schedule_date) : app.schedule_date;
            if (!endDateStr) continue;

            const endDate = new Date(endDateStr);
            const diffMs = Math.max(0, endDate.getTime() - baseDate.getTime());
            const days = diffMs / (1000 * 60 * 60 * 24);

            const stat = statsByProc.get(app.procedure_id) || { totalDays: 0, count: 0 };
            stat.totalDays += days;
            stat.count++;
            statsByProc.set(app.procedure_id, stat);
        }

        return activeProcedures.map(p => {
            const stat = statsByProc.get(p.id);
            return {
                procedureId: p.id,
                procedureName: p.name,
                avgDays: stat && stat.count > 0 ? Math.round(stat.totalDays / stat.count) : null
            };
        });
    }

    /**
     * Gera previsão de atendimento baseada no score e intercalação.
     */
    calculatePatientForecast(app: Appointment | PatientProcess, avgWaitDays?: number | null): string {
        if (app.status === 'Agendado' && app.schedulingData?.date) {
            const [y, m, d] = app.schedulingData.date.split('T')[0].split('-');
            return `${d}/${m}/${y}`;
        }
        if ((app.status === 'Realizado' || app.status === 'Não Realizado') && app.outcomeData?.date) {
            const [y, m, d] = app.outcomeData.date.split('T')[0].split('-');
            return `${d}/${m}/${y}`;
        }

        if (avgWaitDays === undefined) {
            return 'Calculando...';
        }

        if (avgWaitDays === null) {
            return 'Prazo indeterminado';
        }

        const months = Math.round(avgWaitDays / 30);
        if (months < 1) return '< 1 mês';
        if (months === 1) return '1 mês';
        return `${months} meses`;
    }

    private getPriorityScore(a: Appointment | PatientProcess): number {
        if (a.urgencyPriority) return 1000;
        if (a.priority === 'Prioridade Judicial') return 100;
        if (a.priority === 'Prioridade Especial (80+)') return 50;
        if (a.priority === 'Prioridade Legal (60+, PCD, Gestante)') return 25;
        return 0;
    }

    private interleave(prior: any[], normal: any[]): any[] {
        const res = [];
        let iP = 0, iN = 0;
        while (iP < prior.length || iN < normal.length) {
            if (iP < prior.length) res.push(prior[iP++]);
            if (iN < normal.length) res.push(normal[iN++]);
        }
        return res;
    }

    checkQuotaAvailability(procedureId: number): boolean {
        const quota = this.quotaStats().find(q => q.id === procedureId);
        if (!quota) return false;
        return (quota.booked || 0) < (quota.authorized || 0);
    }

    async bookManualSlot(poolId: string | null | undefined, municipalityId: string, booking: {
        patientId: number,
        appointmentId: number,
        location: string,
        startTime: string,
        executorId?: string | number | null,
        isUbsOnDemand?: boolean,
        parentProcedureId?: number | null,
        procedureId?: number | null,
        excludedSlotIds?: string[]
    }) {
        const effectiveMunId = municipalityId || this.context.currentMunicipalityId() || '';

        // 1. Find the first available empty slot for this pool and include executor info
        let query = supabase
            .from('agenda_slots')
            .select('id, executor_id, parent_procedure_id, pool_id')
            .eq('is_available', true);

        if (effectiveMunId) {
            query = query.eq('municipality_id', effectiveMunId);
        }

        if (poolId) {
            query = query.eq('pool_id', poolId);
        }

        if (booking.executorId) {
            query = query.eq('executor_id', booking.executorId);
        }

        if (booking.parentProcedureId) {
            query = query.eq('parent_procedure_id', booking.parentProcedureId);
        }

        if (booking.excludedSlotIds && booking.excludedSlotIds.length > 0) {
            const idsList = booking.excludedSlotIds.filter(Boolean).join(',');
            if (idsList) {
                query = query.not('id', 'in', `(${idsList})`);
            }
        }

        let { data: slot, error: findError } = await query
            .order('start_time', { ascending: true, nullsFirst: false })
            .limit(1)
            .maybeSingle();

        if (findError) throw findError;

        // Se pediu parentProcedureId específico mas não encontrou, tenta slot genérico (sem cirurgia vinculada)
        if (!slot && booking.parentProcedureId) {
            let fallbackQuery = supabase
                .from('agenda_slots')
                .select('id, executor_id, parent_procedure_id, pool_id')
                .eq('is_available', true)
                .is('parent_procedure_id', null);

            if (effectiveMunId) fallbackQuery = fallbackQuery.eq('municipality_id', effectiveMunId);
            if (poolId) fallbackQuery = fallbackQuery.eq('pool_id', poolId);
            if (booking.executorId) fallbackQuery = fallbackQuery.eq('executor_id', booking.executorId);
            if (booking.excludedSlotIds && booking.excludedSlotIds.length > 0) {
                const idsList = booking.excludedSlotIds.filter(Boolean).join(',');
                if (idsList) fallbackQuery = fallbackQuery.not('id', 'in', `(${idsList})`);
            }

            const { data: fallbackSlot, error: fbError } = await fallbackQuery
                .order('start_time', { ascending: true, nullsFirst: false })
                .limit(1)
                .maybeSingle();

            if (!fbError && fallbackSlot) {
                slot = fallbackSlot;
            }
        }

        // Se não achou pelo poolId específico, mas temos o procedureId, busca slot disponível associado à cota do procedimento
        if (!slot && booking.procedureId) {
            let procQuery = supabase
                .from('agenda_slots')
                .select('id, executor_id, parent_procedure_id, pool_id, pool:quota_pools!inner(procedure_id, month_ref)')
                .eq('is_available', true)
                .eq('pool.procedure_id', booking.procedureId);

            if (effectiveMunId) procQuery = procQuery.eq('municipality_id', effectiveMunId);
            if (booking.executorId) procQuery = procQuery.eq('executor_id', booking.executorId);
            if (booking.excludedSlotIds && booking.excludedSlotIds.length > 0) {
                const idsList = booking.excludedSlotIds.filter(Boolean).join(',');
                if (idsList) procQuery = procQuery.not('id', 'in', `(${idsList})`);
            }

            const { data: procSlot, error: procError } = await procQuery
                .order('start_time', { ascending: true, nullsFirst: false })
                .limit(1)
                .maybeSingle();

            if (!procError && procSlot) {
                slot = procSlot;
            }
        }

        // UBS On-Demand: se não há slot pré-existente, criar um ad-hoc automaticamente
        if (!slot && booking.isUbsOnDemand) {
            const targetPoolId = poolId || (slot as any)?.pool_id;
            if (targetPoolId) {
                return await this.createAndFillAdHocSlot(targetPoolId, effectiveMunId, booking);
            }
        }

        if (!slot) throw new Error('Não há vagas disponíveis para distribuição neste procedimento.');

        const executor = this.executorsTable().find(e => e.id === slot.executor_id);
        const executorName = executor ? executor.name : null;

        // 2. Update it and FILL it with the manual data
        const updatePayload: any = {
            patient_id: booking.patientId,
            appointment_id: booking.appointmentId,
            location_name: booking.location,
            start_time: booking.startTime,
            is_available: false
        };

        if (booking.parentProcedureId && !slot.parent_procedure_id) {
            updatePayload.parent_procedure_id = booking.parentProcedureId;
        }

        const { error: updateError } = await supabase
            .from('agenda_slots')
            .update(updatePayload)
            .eq('id', slot.id);

        if (updateError) throw updateError;

        // 2.5 Update the appointment record with the executor data
        const { error: appointmentUpdateError } = await supabase
            .from('appointments')
            .update({
                executor_id: slot.executor_id,
                executor_name: executorName
            })
            .eq('id', booking.appointmentId);

        if (appointmentUpdateError) {
            console.warn('[QuotaService] Falha ao sincronizar executor no agendamento:', appointmentUpdateError);
        }

        // A trigger no Supabase 'trg_sync_pool_booked_count' já cuida de atualizar a cota
        // com base na mudança de status do agendamento ('Agendado', 'Realizado'). 
        // Não é necessário incrementar manualmente.

        return slot.id;
    }

    /**
     * [UBS On-Demand] Cria e preenche um slot ad-hoc quando há pool mas nenhum slot disponível.
     * O slot é criado já preenchido (is_available: false) para evitar race conditions.
     */
    private async createAndFillAdHocSlot(poolId: string, municipalityId: string, booking: {
        patientId: number,
        appointmentId: number,
        location: string,
        startTime: string,
        executorId?: string | number | null
    }): Promise<string> {
        const { data: newSlot, error } = await supabase
            .from('agenda_slots')
            .insert({
                pool_id: poolId,
                municipality_id: municipalityId,
                location_name: booking.location || 'UBS',
                start_time: booking.startTime,
                end_time: booking.startTime,
                executor_id: booking.executorId || null,
                patient_id: booking.patientId,
                appointment_id: booking.appointmentId,
                is_available: false,
                is_active: true
            })
            .select('id')
            .single();

        if (error) throw error;

        console.log('[QuotaService] UBS Ad-Hoc slot criado (com pool):', newSlot.id);
        return newSlot.id;
    }

    /**
     * [UBS On-Demand] Cria um slot estatístico avulso quando não existe nenhum pool mensal.
     * Não propaga erros - o agendamento já foi salvo e o slot é apenas para rastreio.
     */
    async createUbsAdHocSlot(appointment: { id?: number, patientId: number, municipalityId: string }, formVal: {
        scheduleDate?: string,
        scheduleTime?: string,
        scheduleLocation?: string,
        executorId?: string | number | null
    }): Promise<void> {
        const startTime = `${formVal.scheduleDate}T${formVal.scheduleTime || '08:00'}:00`;

        const { error } = await supabase
            .from('agenda_slots')
            .insert({
                pool_id: null,
                municipality_id: appointment.municipalityId,
                location_name: formVal.scheduleLocation || 'UBS',
                start_time: startTime,
                end_time: startTime,
                executor_id: formVal.executorId || null,
                patient_id: appointment.patientId,
                appointment_id: appointment.id || null,
                is_available: false,
                is_active: true
            });

        if (error) {
            // Não bloquear o fluxo - o agendamento já foi salvo
            console.warn('[QuotaService] UBS Ad-Hoc slot (sem pool) não criado:', error.message);
        } else {
            console.log('[QuotaService] UBS Ad-Hoc slot (sem pool) criado para appt:', appointment.id);
        }
    }

    async bookSlot(slotId: string, patientId: number, appointmentId: number) {
        const { error } = await supabase
            .from('agenda_slots')
            .update({
                patient_id: patientId,
                appointment_id: appointmentId,
                is_available: false
            })
            .eq('id', slotId);

        if (error) throw error;

        // Increment booked_count removido: A trigger trg_sync_pool_booked_count 
        // já atualiza as quotas do pool de acordo com as Alterações de status dos agendamentos.

        return { id: slotId, success: true };
    }

    // --- LOCATION MANAGEMENT ---

    async getLocations(municipalityId: string) {
        const { data, error } = await supabase
            .from('procedure_locations')
            .select('*')
            .eq('municipality_id', municipalityId)
            .neq('is_active', false)
            .order('name');

        if (error) throw error;
        const mapped = data.map((l: any) => this.mapDbToLocation(l));
        this.locationsTable.set(mapped);
        return mapped;
    }

    async addLocation(loc: Omit<ProcedureLocation, 'id' | 'isActive'>) {
        const { data, error } = await supabase
            .from('procedure_locations')
            .insert({
                municipality_id: loc.municipalityId,
                name: loc.name,
                address: loc.address,
                reference: loc.reference,
                phone: loc.phone,
                n8n_webhook_url: (loc as any).n8nWebhookUrl,
                is_ubs: (loc as any).isUbs ?? false,
                is_active: true
            })
            .select()
            .maybeSingle();

        if (error) throw error;

        // Sync local state
        const newLoc = this.mapDbToLocation(data);
        this.locationsTable.update(list => [...list, newLoc]);

        return data;
    }

    async updateLocation(id: string, updates: Partial<ProcedureLocation>, oldName?: string) {
        const { data, error } = await supabase
            .from('procedure_locations')
            .update({
                name: updates.name,
                address: updates.address,
                reference: updates.reference,
                phone: updates.phone,
                n8n_webhook_url: updates.n8nWebhookUrl,
                is_ubs: updates.isUbs,
                daily_quotas: updates.dailyQuotas
            })
            .eq('id', id)
            .select()
            .maybeSingle();

        if (error) throw error;

        // Sync local state
        const updatedLoc = this.mapDbToLocation(data);
        this.locationsTable.update(list => list.map(l => l.id === id ? updatedLoc : l));

        // Cascade update name in slots and appointments if name changed
        if (updates.name && oldName && updates.name.trim().toUpperCase() !== oldName.trim().toUpperCase()) {
            const newName = updates.name.trim();
            const oldNameNormalized = oldName.trim();

            // 1. Update Slots (Vacancies)
            await supabase
                .from('agenda_slots')
                .update({ location_name: newName })
                .or(`location_name.ilike.${oldNameNormalized},location_name.ilike.%${oldNameNormalized.replace(/ \(.*\)$/, '')}%`);

            // 2. Update Appointments
            await supabase
                .from('appointments')
                .update({ schedule_location: newName })
                .or(`schedule_location.ilike.${oldNameNormalized},schedule_location.ilike.%${oldNameNormalized.replace(/ \(.*\)$/, '')}%`);

            await supabase
                .from('appointments')
                .update({ outcome_location: newName })
                .or(`outcome_location.ilike.${oldNameNormalized},outcome_location.ilike.%${oldNameNormalized.replace(/ \(.*\)$/, '')}%`);

            // 3. Update Local State (via apptService)
            this.apptService.appointmentsTable.update(list => list.map(app => {
                let changed = false;
                const newApp = { ...app };
                const oldBase = oldNameNormalized.toUpperCase().replace(/ \(.*\)$/, '').trim();

                const matches = (loc: string | undefined) => {
                    if (!loc) return false;
                    const l = loc.trim().toUpperCase();
                    return l === oldNameNormalized.toUpperCase() || l.includes(oldBase) || oldBase.includes(l);
                };

                if (matches(app.schedulingData?.location)) {
                    newApp.schedulingData = { ...app.schedulingData, location: newName };
                    changed = true;
                }
                if (matches(app.outcomeData?.location)) {
                    newApp.outcomeData = { ...app.outcomeData, location: newName };
                    changed = true;
                }

                return changed ? newApp : app;
            }));
        }

        return data;
    }

    async getProcedureLocations(municipalityId?: string): Promise<any[]> {
        let query = supabase.from('procedure_locations').select('*').neq('is_active', false);
        if (municipalityId) {
            query = query.eq('municipality_id', municipalityId);
        }
        const { data, error } = await query;
        if (error) {
            console.error('[QuotaService] Error fetching locations', error);
            return [];
        }
        return data.map(l => this.mapDbToLocation(l));
    }

    async deleteLocation(id: string) {
        const { error } = await supabase.from('procedure_locations').delete().eq('id', id);
        if (error) throw error;
        this.locationsTable.update(list => list.filter(l => l.id !== id));
    }

    async getProcedureLocationByName(name: string, municipalityId?: string): Promise<any | null> {
        const cleanName = name.trim();
        if (!cleanName) return null;

        const performSearch = async (munId?: string) => {
            let q = supabase.from('procedure_locations').select('*');
            if (munId) q = q.eq('municipality_id', munId);

            const { data: firstTry } = await q.ilike('name', cleanName).maybeSingle();
            if (firstTry) return firstTry;

            const { data: secondTry } = await q.ilike('name', `%${cleanName}%`).limit(1).maybeSingle();
            if (secondTry) return secondTry;

            let qAll = supabase.from('procedure_locations').select('*');
            if (munId) qAll = qAll.eq('municipality_id', munId);
            const { data: all } = await qAll;

            if (all && all.length > 0) {
                const target = cleanName.toUpperCase().replace(/[\s.\-]+/g, '');
                const found = all.find(l => l.name.toUpperCase().replace(/[\s.\-]+/g, '') === target);
                if (found) return found;

                const partial = all.find(l => {
                    const lNorm = l.name.toUpperCase().replace(/[\s.\-]+/g, '');
                    return lNorm.includes(target) || target.includes(lNorm);
                });
                if (partial) return partial;

                const targetWords = cleanName.toUpperCase().split(/\s+/).filter(w => w.length > 2);
                const wordMatch = all.find(l => {
                    const lWords = l.name.toUpperCase().split(/\s+/);
                    return targetWords.every(tw => lWords.some(lw => lw.includes(tw) || tw.includes(lw)));
                });
                if (wordMatch) return wordMatch;
            }
            return null;
        };

        const effectiveMunId = municipalityId || this.context.currentMunicipalityId() || '';

        if (effectiveMunId) {
            const result = await performSearch(effectiveMunId);
            if (result) return this.mapDbToLocation(result);
            // Isolamento intermunicipal estrito: se um município foi informado ou está ativo na sessão,
            // JAMAIS fazer busca global para evitar que locais/hospitais de outro município vazem para este paciente/atendimento.
            return null;
        }

        const globalResult = await performSearch();
        return globalResult ? this.mapDbToLocation(globalResult) : null;
    }

    async getAllUnfilledSlots(municipalityId: string | null, dashboardDate: Date): Promise<any[]> {
        const month = dashboardDate.getMonth() + 1;
        const year = dashboardDate.getFullYear();
        const targetMonthRef = `${year}-${String(month).padStart(2, '0')}`;

        const { data, error } = await supabase
            .from('agenda_slots')
            .select(`
                *,
                quota_pools!inner(
                    month_ref,
                    procedure_id,
                    procedures(
                        name,
                        category
                    )
                )
            `)
            .eq('municipality_id', municipalityId)
            .eq('is_available', true)
            .lt('start_time', new Date().toISOString());

        if (error) {
            console.error('[QuotaService] Erro ao buscar vagas não preenchidas:', error);
            return [];
        }

        const filteredData = (data || []).filter((s: any) => s.quota_pools?.month_ref?.startsWith(targetMonthRef));

        return filteredData.map((slot: any) => {
            const raw = slot.start_time || '';
            const timeStr = raw.includes('T') ? raw.split('T')[1].substring(0, 5) : (raw ? raw.substring(0, 5) : '--:--');
            const dateParts = raw.includes('T') ? raw.split('T')[0].split('-') : [];
            const dateStr = dateParts.length === 3 ? `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}` : (raw ? new Date(raw).toLocaleDateString('pt-BR') : '--/--/----');

            return {
                procedureName: slot.quota_pools?.procedures?.name || 'Desconhecido',
                category: slot.quota_pools?.procedures?.category || 'GERAL',
                location: slot.location_name || 'Não especificado',
                date: dateStr,
                time: timeStr,
                poolId: slot.pool_id,
                startTime: slot.start_time,
                id: slot.id
            };
        });
    }

    /**
     * Duplica um procedimento existente.
     */
    async duplicateProcedure(id: number, municipalityId: string | null, newName?: string) {
        try {
            this.loadingService.set(true);
            // 1. Buscar o original com suas etapas
            const { data: original, error: fetchError } = await supabase
                .from('procedures')
                .select('*, surgery_steps_config!parent_procedure_id(*)')
                .eq('id', id)
                .single();

            if (fetchError || !original) throw fetchError;

            // 2. Preparar clone
            const { id: _, created_at: __, audit_trail: ___, surgery_steps_config: steps, ...cloneData } = original;
            const newProcedure = {
                ...cloneData,
                name: newName || `${original.name} (Cópia)`,
                created_at: new Date().toISOString(),
                audit_trail: []
            };

            // 3. Inserir procedimento
            const { data: inserted, error: insertError } = await supabase
                .from('procedures')
                .insert(newProcedure)
                .select()
                .single();

            if (insertError) throw insertError;

            // 4. Inserir steps se houver
            if (steps && steps.length > 0) {
                const stepsToInsert = steps.map((s: any) => ({
                    parent_procedure_id: inserted.id,
                    step_procedure_id: s.step_procedure_id,
                    step_order: s.step_order
                }));
                const { error: stepsError } = await supabase.from('surgery_steps_config').insert(stepsToInsert);
                if (stepsError) throw stepsError;
            }

            // 5. Duplicar locais de imagem (região anatômica)
            if (original.is_imaging) {
                const locations = await this.getImagingLocations(id);
                if (locations && locations.length > 0) {
                    await this.updateImagingLocations(inserted.id, locations);
                }
            }

            // Adiciona ao estado local (Signal) para refletir na UI instantaneamente
            const newProc: Procedure = {
                id: inserted.id,
                name: inserted.name,
                category: inserted.category,
                complexity: inserted.complexity,
                monthlyLimit: inserted.monthly_limit,
                requiresPreSurgery: inserted.requires_pre_surgery,
                preSurgeryCount: inserted.pre_surgery_count,
                latestProtocol: inserted.latest_protocol,
                municipalityId: inserted.municipality_id,
                isImaging: inserted.is_imaging,
                isOnDemand: inserted.is_on_demand,
                isGroup: inserted.is_group ?? false,
                auditTrail: inserted.audit_trail,
                hasOrientation: inserted.has_orientation,
                orientationText: inserted.orientation_text
            };

            this.proceduresTable.update(current => [...current, newProc]);

            this.toast.show('Procedimento duplicado com sucesso.', 'success');
            await this.refreshProcedures(municipalityId);
            return inserted;
        } catch (err: any) {
            console.error('[QuotaService] Erro ao duplicar procedimento:', err);
            this.toast.show(err.message || 'Erro ao duplicar procedimento', 'error');
            return null;
        } finally {
            this.loadingService.set(false);
        }
    }

    // --- EXAM GROUP MEMBERS (Agrupamento de Exames) ---

    /**
     * Busca todos os subexames de um Exame de Grupo, com o nome do filho desnormalizado.
     */
    async getExamGroupMembers(parentId: number): Promise<ExamGroupMember[]> {
        const { data, error } = await supabase
            .from('exam_group_members')
            .select(`
                id,
                parent_procedure_id,
                sub_exam_name,
                visual_category,
                display_order
            `)
            .eq('parent_procedure_id', parentId)
            .order('visual_category', { ascending: true })
            .order('display_order', { ascending: true });

        if (error) throw error;
        return (data || []).map((d: any) => ({
            id: d.id,
            parentProcedureId: d.parent_procedure_id,
            subExamName: d.sub_exam_name,
            visualCategory: d.visual_category,
            displayOrder: d.display_order
        })) as ExamGroupMember[];
    }

    /**
     * Adiciona um subexame a um Exame de Grupo.
     * A categoria visual é obrigatória.
     */
    async addExamGroupMember(member: Omit<ExamGroupMember, 'id'>): Promise<ExamGroupMember> {
        const { data, error } = await supabase
            .from('exam_group_members')
            .insert({
                parent_procedure_id: member.parentProcedureId,
                sub_exam_name: member.subExamName,
                visual_category: member.visualCategory,
                display_order: member.displayOrder
            })
            .select()
            .single();

        if (error) throw error;
        return {
            id: data.id,
            parentProcedureId: data.parent_procedure_id,
            subExamName: data.sub_exam_name,
            visualCategory: data.visual_category,
            displayOrder: data.display_order
        };
    }

    /**
     * Remove um subexame de um Exame de Grupo pelo ID do vínculo.
     */
    async removeExamGroupMember(id: number): Promise<void> {
        const { error } = await supabase
            .from('exam_group_members')
            .delete()
            .eq('id', id);

        if (error) throw error;
    }

    /**
     * Atualiza a ordem de exibição de um subexame dentro do grupo.
     */
    async updateExamGroupMemberOrder(id: number, displayOrder: number): Promise<void> {
        const { error } = await supabase
            .from('exam_group_members')
            .update({ display_order: displayOrder })
            .eq('id', id);

        if (error) throw error;
    }

    /**
     * Substitui todos os subexames de um procedimento.
     */
    async updateExamGroupMembers(parentId: number, members: Omit<import('../models/procedure.model').ExamGroupMember, 'id' | 'parentProcedureId'>[]): Promise<void> {
        // 1. Remove antigos
        const { error: delError } = await supabase.from('exam_group_members').delete().eq('parent_procedure_id', parentId);
        if (delError) {
            console.error('Delete exam_group_members error:', delError);
            throw new Error(`Erro ao remover subexames antigos: ${delError.message}`);
        }

        // 2. Insere novos se houver
        if (members && members.length > 0) {
            const inserts = members.map(m => ({
                parent_procedure_id: parentId,
                sub_exam_name: m.subExamName,
                visual_category: m.visualCategory,
                display_order: m.displayOrder
            }));
            const { error } = await supabase.from('exam_group_members').insert(inserts);
            if (error) throw error;
        }
    }

    // --- IMAGING EXAM LOCATIONS (Regiões Anatômicas) ---

    async getImagingLocations(procedureId: number): Promise<string[]> {
        const { data, error } = await supabase
            .from('procedure_imaging_locations')
            .select('location_name')
            .eq('procedure_id', procedureId)
            .order('display_order', { ascending: true });

        if (error) throw error;
        return (data || []).map((d: any) => d.location_name);
    }

    async updateImagingLocations(procedureId: number, locations: string[]): Promise<void> {
        // 1. Remove antigos
        const { error: delError } = await supabase.from('procedure_imaging_locations').delete().eq('procedure_id', procedureId);
        if (delError) {
            console.error('Delete imaging_locations error:', delError);
            throw new Error(`Erro ao remover locais antigos: ${delError.message}`);
        }

        // 2. Insere novos se houver
        if (locations && locations.length > 0) {
            const inserts = locations.map((loc, index) => ({
                procedure_id: procedureId,
                location_name: loc,
                display_order: index + 1
            }));
            const { error } = await supabase.from('procedure_imaging_locations').insert(inserts);
            if (error) throw error;
        }
    }

    /**
     * Lê uma planilha de subexames e retorna os itens para importação.
     * Formato esperado: "Grupo" | "exame"
     */
    async parseSubExamSpreadsheet(file: File): Promise<{ category: string, subExamName: string }[]> {
        const XLSX = await import('xlsx');

        const buffer = await file.arrayBuffer();
        const wb = XLSX.read(buffer, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

        if (!raw || raw.length < 2) {
            throw new Error('Planilha vazia ou sem dados além do cabeçalho.');
        }

        // Detecta índices de colunas baseado no cabeçalho (case-insensitive)
        const header = (raw[0] as string[]).map(h => String(h).toLowerCase().trim());
        const colGroup = header.findIndex(h => h.includes('grupo') || h.includes('categoria'));
        const colExam = header.findIndex(h => h.includes('exame') || h.includes('nome'));

        if (colGroup < 0 || colExam < 0) {
            throw new Error('Colunas "Grupo" (categoria) e "exame" (nome do subexame) não encontradas.');
        }

        const items: { category: string, subExamName: string }[] = [];
        for (let i = 1; i < raw.length; i++) {
            const row = raw[i];
            const category = String(row[colGroup] ?? '').trim();
            const subExamName = String(row[colExam] ?? '').trim();

            if (category && subExamName) {
                items.push({
                    category,
                    subExamName
                });
            }
        }

        if (items.length === 0) {
            throw new Error('Nenhum dado válido encontrado nas colunas "Grupo" e "exame".');
        }

        return items;
    }

    // ─── IMPORTAÇÃO INTELIGENTE VIA PLANILHA ───────────────────────────

    /**
     * Lê um arquivo XLSX e retorna as linhas para o Modal de Revisão.
     * Cruza cada linha com os procedimentos já existentes no banco para
     * determinar se é um cadastro novo ou uma atualização de cota.
     *
     * Colunas esperadas: Procedimento | Categoria | Cota | Ente Executor (opcional)
     *
     * @param file            Arquivo XLSX selecionado pelo operador
     * @param municipalityId  ID do município para busca de executores (opcional)
     */
    async parseSpreadsheet(file: File, municipalityId?: string): Promise<ImportReviewRow[]> {
        const XLSX = await import('xlsx');

        const buffer = await file.arrayBuffer();
        const wb = XLSX.read(buffer, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

        if (!raw || raw.length < 2) {
            throw new Error('Planilha vazia ou sem dados além do cabeçalho.');
        }

        // Detecta índices de colunas baseado no cabeçalho (case-insensitive)
        const header = (raw[0] as string[]).map(h => String(h).toLowerCase().trim());
        const colName = header.findIndex(h => h.includes('procedimento') || h.includes('nome'));
        const colCat = header.findIndex(h => h.includes('categoria'));
        const colQuota = header.findIndex(h =>
            h.includes('cota') || h.includes('limite') || h.includes('vagas') || h.includes('quantidade')
        );
        const colExec = header.findIndex(h => h.includes('executor') || h.includes('ente'));

        if (colName < 0 || colQuota < 0) {
            throw new Error('Coluna "Procedimento" e/ou "Cota" não encontradas. Verifique o modelo.');
        }

        // Se a planilha tem coluna de executor, busca os executores do município
        const executorMap = new Map<string, string>(); // nome.toLowerCase() -> id
        if (colExec >= 0 && municipalityId) {
            const { data: execs } = await supabase
                .from('executors')
                .select('id, name')
                .eq('municipality_id', municipalityId);
            if (execs) {
                execs.forEach(e => executorMap.set(e.name.toLowerCase().trim(), e.id));
            }
        }

        const normalizeCategory = (cat: string): string => {
            const c = cat.toLowerCase().trim();
            if (c.includes('consulta básica') || c.includes('consulta basica')) return 'CONSULTA BÁSICA';
            if (c.includes('consulta especializada')) return 'CONSULTA ESPECIALIZADA';
            if (c.includes('exames') || c.includes('exame')) return 'EXAMES';
            if (c.includes('cirurgia')) return 'CIRURGIA';
            if (c.includes('pré-operatório') || c.includes('pre-operatorio')) return 'PRÉ-OPERATÓRIO';
            // Fallback: uppercase for consistency
            return cat.toUpperCase().trim();
        };

        const existingProcs = this.proceduresTable();
        const rows: ImportReviewRow[] = [];

        for (let i = 1; i < raw.length; i++) {
            const row = raw[i] as any[];
            const rawName = String(row[colName] ?? '').trim();
            const rawCatInput = colCat >= 0 ? String(row[colCat] ?? '').trim() : '';
            const rawCat = normalizeCategory(rawCatInput || 'CONSULTA BÁSICA');
            const rawQuota = Number(row[colQuota]) || 0;

            if (!rawName) continue; // Pula linhas em branco

            const procKey = rawName.toLowerCase().trim();
            const rawExec = colExec >= 0 ? String(row[colExec] ?? '').trim() : '';
            const resolvedExecId = rawExec ? (executorMap.get(rawExec.toLowerCase()) ?? null) : null;

            // Cruzamento: procura por nome exato (case-insensitive)
            const matched = existingProcs.find(p =>
                p.name.toLowerCase().trim() === procKey
            );

            rows.push({
                executorName: rawExec || undefined,
                resolvedExecutorId: resolvedExecId,
                distributions: (resolvedExecId || rawExec) ? [{
                    executorId: resolvedExecId,
                    executorName: rawExec,
                    quantity: rawQuota
                }] : [],
                rawName,
                rawCategory: rawCat,
                rawQuota,
                resolvedName: rawName,
                resolvedCategory: matched?.category || rawCat,
                resolvedQuota: rawQuota,
                isNew: !matched,
                shouldCreate: !matched, // toggle padrão ativado para novos
                matchedProcedureId: matched?.id ?? null,
                isEditing: false,
                importMode: 'replace'
            });
        }

        if (rows.length === 0) {
            throw new Error('Nenhuma linha válida encontrada na planilha.');
        }

        return rows;
    }

    /**
     * Aplica as linhas revisadas pelo operador:
     * 1. Cria novos procedimentos quando `isNew && shouldCreate`
     * 2. Faz upsert no quota_pool (soma a cota) para todos os procedimentos confirmados
     * 3. Se a linha tiver um `resolvedExecutorId` válido, distribui as vagas via RPC
     */
    async confirmImport(
        rows: ImportReviewRow[],
        municipalityId: string,
        operatorId: number,
        monthRef: string,
        autoRepeat: boolean = true
    ): Promise<{ created: number; updated: number; distributed: number }> {
        const normalizedMonth = monthRef.length === 7 ? `${monthRef}-01` : monthRef;
        let created = 0;
        let updated = 0;
        let distributed = 0;

        const sessionReplacedPools = new Set<string>();

        // 1. Agrupar logicamente por nome de procedimento para consolidar a cota total
        const groups = new Map<string, ImportReviewRow[]>();
        for (const row of rows) {
            const key = row.resolvedName.toLowerCase().trim();
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key)!.push(row);
        }

        for (const [_, groupRows] of groups) {
            const baseRow = groupRows[0];
            // Pula procedimentos novos que o operador optou por não criar
            if (baseRow.isNew && !baseRow.shouldCreate) continue;

            // Calcula a cota total somada do grupo
            const totalGroupQuota = groupRows.reduce((sum, r) => sum + r.resolvedQuota, 0);
            if (totalGroupQuota <= 0) continue;

            let procedureId = baseRow.matchedProcedureId;

            // 1. Criar procedimento novo se necessário (usando dados da primeira linha do grupo)
            if (baseRow.isNew && baseRow.shouldCreate) {
                const cat = (baseRow.resolvedCategory || '').toUpperCase();
                const isImagingAuto = cat.includes('EXAME') || cat.includes('IMAGEM') || cat.includes('RAIO-X') || cat.includes('RADIO');

                const res = await this.addProcedure({
                    name: baseRow.resolvedName,
                    category: baseRow.resolvedCategory,
                    complexity: 'Baixa',
                    monthlyLimit: totalGroupQuota,
                    municipalityId,
                    requiresPreSurgery: false,
                    preSurgeryCount: 0,
                    isImaging: isImagingAuto,
                    isOnDemand: false,
                    isGroup: false,
                    hasOrientation: false,
                    orientationText: ''
                }, operatorId, municipalityId);
                procedureId = res.procedure.id;
                created++;
            }

            if (!procedureId) continue;

            // 2. Upsert no quota_pool
            let poolId: string | null = null;
            const { data: existingPool } = await supabase
                .from('quota_pools')
                .select('id, total_authorized')
                .eq('procedure_id', procedureId)
                .eq('municipality_id', municipalityId)
                .eq('month_ref', normalizedMonth)
                .maybeSingle();

            if (existingPool) {
                // Se o modo for 'replace' e ainda não substituímos este pool nesta sessão, resetamos conforme o valor total do grupo.
                const shouldReplace = baseRow.importMode === 'replace' && !sessionReplacedPools.has(existingPool.id);

                if (shouldReplace) {
                    // Limpar slots e templates e resetar count via método centralizado
                    const bookedCount = await this.clearPoolForRewrite(existingPool.id);

                    // REGRA: Importação por planilha APENAS AUMENTA a cota total.
                    const newLimit = Math.max(existingPool.total_authorized, totalGroupQuota);

                    await supabase
                        .from('quota_pools')
                        .update({
                            total_authorized: newLimit,
                            distributed_count: bookedCount
                        })
                        .eq('id', existingPool.id);

                    sessionReplacedPools.add(existingPool.id);
                } else {
                    // Modo 'add' ou já substituído nesta sessão: soma ao existente
                    await supabase
                        .from('quota_pools')
                        .update({
                            total_authorized: existingPool.total_authorized + totalGroupQuota
                        })
                        .eq('id', existingPool.id);
                }

                poolId = existingPool.id;
            } else {
                // Cria um novo pool para o mês com o total do grupo
                const { data: newPool } = await supabase
                    .from('quota_pools')
                    .insert({
                        procedure_id: procedureId,
                        municipality_id: municipalityId,
                        month_ref: normalizedMonth,
                        total_authorized: totalGroupQuota,
                        distributed_count: 0
                    })
                    .select('id')
                    .single();
                poolId = newPool?.id ?? null;
            }
            updated++;

            // 3. Distribuição (Agrega todas as distribuições de todos as linhas do grupo)
            const allDistributions: any[] = [];
            for (const r of groupRows) {
                if (r.distributions && r.distributions.length > 0) {
                    allDistributions.push(...r.distributions.filter(d => !!d.executorId));
                } else if (r.resolvedExecutorId) {
                    allDistributions.push({
                        executorId: r.resolvedExecutorId,
                        quantity: r.resolvedQuota
                    });
                }
            }

            if (poolId && allDistributions.length > 0) {
                try {
                    // Distribui as vagas na cota para todos os executores agregados do grupo
                    await this.distributeMultiExecutor(
                        poolId,
                        allDistributions.map(d => ({ executorId: d.executorId, quantity: d.quantity })),
                        municipalityId,
                        autoRepeat
                    );
                    distributed += totalGroupQuota;
                } catch (e) {
                    console.error(`[QuotaService] Erro ao distribuir cota agregada para ${baseRow.resolvedName}:`, e);
                }
            }
        }

        return { created, updated, distributed };
    }

    /**
     * Case-insenstive category normalization helper
     */
    private normalizeCategory(cat: string): string {
        return (cat || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase().trim();
    }

    /**
     * On-demand slot distribution for a specific professional.
     * Called by the UBS Regulator from the scheduling panel.
     *
     * @param professionalId - UUID of the professional opening slots
     * @param procedureId    - Numeric procedure ID
     * @param locationId     - UUID of the UBS procedure_location
     * @param amount         - How many patients to promote to "Em Agendamento"
     */
    async distributeProfessionalDemand(
        professionalId: string,
        procedureId: number,
        locationId: string,
        amount: number,
        date: string,
        time: string
    ): Promise<{ promoted: number; mode: string }> {
        const municipality = this.context.currentMunicipality();
        const municipalityId = municipality?.id;

        if (!municipalityId) throw new Error('Município não identificado.');
        if (amount <= 0) throw new Error('A quantidade de vagas deve ser maior que zero.');

        this.loadingService.set(true);
        try {
            // [Opção A]: Grava a vaga "Fantasma" para o painel de monitoramento
            const { error: upsertErr } = await supabase.rpc('increment_ubs_daily_quota', {
                p_municipality_id: municipalityId,
                p_location_id: locationId,
                p_professional_id: professionalId,
                p_procedure_id: procedureId,
                p_date: date,
                p_time: time,
                p_amount: amount
            });
            if (upsertErr) {
                console.error('[QuotaService] Erro crítico ao gravar vaga diária (Bolsão):', upsertErr);
                throw new Error('Falha ao registrar o bolsão de vagas no banco de dados. A distribuição foi cancelada para garantir integridade.');
            }

            // [Opção B]: Distribuição Segura e Prioritária via RPC (PostgreSQL)
            // Esta função já resolve ACS vs Clássico, Isolamento de UBS e Prioridades Legais.
            const { data, error } = await supabase.rpc('distribute_professional_demand', {
                p_professional_id: professionalId,
                p_procedure_id: procedureId,
                p_location_id: locationId,
                p_amount: amount,
                p_municipality_id: municipalityId,
                p_date: date,
                p_time: time
            });

            if (error) throw error;
            if (data?.success === false) throw new Error(data.error);

            const promoted = data?.promoted || 0;
            const mode = data?.mode || 'N/A';

            // Notificação UX
            if (promoted > 0) {
                this.toast.show(`${promoted} paciente(s) promovido(s) via ${mode}.`, 'success');
            } else {
                this.toast.show(`Vagas criadas. Ninguém aguardando nesta UBS/Equipe.`, 'info');
            }

            // Auditoria
            const op = this.context.currentOperator();
            await this.auditService.logAction(
                'DISTRIBUTE_PROFESSIONAL_DEMAND_RPC',
                `Distribuição UBS: ${amount} vagas (${promoted} alocados) via ${mode}`,
                { professionalId, procedureId, locationId, amount, date, time, mode },
                op?.name || 'SISTEMA',
                `UBS-RPC-${Date.now()}`
            );

            this.apptService.triggerQueueRefresh();
            return { promoted, mode };
        } catch (err: any) {
            console.error('[QuotaService] Erro na distribuição UBS:', err);
            this.toast.show(err.message || 'Erro ao distribuir vagas.', 'error');
            throw err;
        } finally {
            this.loadingService.set(false);
        }
    }

    /**
     * Retorna o total de vagas já ocupadas/ofertadas para um profissional e procedimento no dia.
     */
    async getAlreadyDistributedCount(profId: string, procId: number, locationId: string, date: string): Promise<number> {
        const municipalityId = this.context.currentMunicipalityId();
        if (!municipalityId || !profId || !procId || !locationId || !date) return 0;

        const numProcId = Number(procId);
        if (isNaN(numProcId) || numProcId === 0) return 0;

        // 1. Quantos pacientes realmente ocuparam essa cota hoje
        const { count, error } = await supabase
            .from('appointments')
            .select('*', { count: 'exact', head: true })
            .eq('municipality_id', municipalityId)
            .eq('allocated_professional_id', profId)
            .eq('procedure_id', numProcId)
            .eq('allocated_date', date)
            .or('status.eq.Em Agendamento,status.eq.Agendado,status.eq.Realizado');

        if (error) {
            console.error('[QuotaService] Error fetching already distributed count:', error);
        }

        // 2. Quanto o regulador pre-injetou para esse dia
        const { data: ghostData, error: ghostError } = await supabase
            .from('ubs_daily_quotas')
            .select('allocated_amount')
            .eq('municipality_id', municipalityId)
            .eq('location_id', locationId) // Isolamento por UBS
            .eq('professional_id', profId)
            .eq('procedure_id', numProcId)
            .eq('target_date', date);

        if (ghostError) {
            console.error('[QuotaService] Error fetching ghost quota:', ghostError);
        }

        const realCount = count || 0;
        // Somar todas as vagas injetadas no dia (podem haver múltiplos turnos/horários)
        const injectedCount = (ghostData || []).reduce((acc: number, curr: any) => acc + Number(curr.allocated_amount || 0), 0);

        return Math.max(realCount, injectedCount);
    }
    async updateUbsDailyQuota(id: string, amount: number) {
        return await supabase.from('ubs_daily_quotas').update({ allocated_amount: amount }).eq('id', id);
    }
    async deleteUbsDailyQuota(id: string) {
        return await supabase.from('ubs_daily_quotas').delete().eq('id', id);
    }
}

